import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Package2, Plus, Search, ChevronDown, Clock, Calendar, ShoppingCart, CheckCircle2, AlertCircle, Tag, Trash2, ArrowUp, ArrowDown, BarChart3, DollarSign, Minus, CreditCard, X, Check, Mail, Phone, Store, ChevronRight, ChevronLeft, Palette, Building2, Users2, MoreVertical, MoreHorizontal, FileText, Layers, CalendarClock } from 'lucide-react';
import { format } from 'date-fns';
import { useBusiness } from '../contexts/BusinessContext';
import { getStockItems, StockItem, getStockHistory, StockHistoryEntry, getTransactionHistory, TransactionHistoryEntry, deleteStockHistoryEntry, deleteGroupedTransaction } from '../lib/stockItems';
import { getSuppliers, Customer, updateCustomer } from '../lib/customers';
import { DiscountCodeService } from '../services/discountCodeService';
import { supabase } from '../lib/supabase';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { ProductPhotoService } from '../services/productPhotoService';
import WithdrawStockModal from '../components/WithdrawStockModal';
import NewStockItemModal from '../components/NewStockItemModal';
import ReceiveStockModal from '../components/ReceiveStockModal';
import PlaceOrderModal from '../components/PlaceOrderModal';
import CreateOrderModal from '../components/CreateOrderModal';
import SellableItemsModal from '../components/SellableItemsModal';
import SelectCustomerModal from '../components/manage/SelectCustomerModal';
import RecordProductSalePaymentModal from '../components/RecordProductSalePaymentModal';
import SendInvoiceModal from '../components/SendInvoiceModal';
import { createProductSale, quoteCounterSale, toCounterSaleDiscount, CounterSaleQuote } from '../lib/productSales';
import { sendGmailEmail } from '../lib/email';
import { toast } from '../components/toasts/SimpleToast';
import PurchaseOrderDetailModal from '../components/PurchaseOrderDetailModal';
import { getOrders, Order, getOnOrderQuantities } from '../lib/purchaseOrders';
import PermissionGuard from '../components/PermissionGuard';
import AddSupplierModal from '../components/AddSupplierModal';
import ProductGroupModal from '../components/ProductGroupModal';
import { getProductGroupsWithImages, ProductGroupWithImages } from '../lib/productGroups';
import CustomerOrdersTab from '../components/orders/CustomerOrdersTab';
import RentalsTab from '../components/orders/RentalsTab';
import InventoryBottomNav from '../components/stock/InventoryBottomNav';

type InventoryEntry =
  | { type: 'item'; item: StockItem }
  | { type: 'group'; group: ProductGroupWithImages; variants: StockItem[] };

function Stock() {
  const { selectedBusiness } = useBusiness();
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [isReceiveStockModalOpen, setIsReceiveStockModalOpen] = useState(false);
  const [isWithdrawStockModalOpen, setIsWithdrawStockModalOpen] = useState(false);
  const [isSellableModalOpen, setIsSellableModalOpen] = useState(false);
  const [isPlaceOrderModalOpen, setIsPlaceOrderModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<StockItem | null>(null);
  const [productGroups, setProductGroups] = useState<ProductGroupWithImages[]>([]);
  const [viewingGroup, setViewingGroup] = useState<{ group: ProductGroupWithImages; variants: StockItem[] } | null>(null);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [currentView, setCurrentView] = useState<'products' | 'sell' | 'orders' | 'customer-orders' | 'rentals' | 'history' | 'metrics' | 'suppliers'>('products');
  const location = useLocation();
  const navigate = useNavigate();

  // Bumped by the bottom nav's [+] → Order to open the New Order modal
  // inside CustomerOrdersTab after switching to that view.
  const [newOrderSignal, setNewOrderSignal] = useState(0);

  // Top ⋯ overflow menu (Rentals / Suppliers / Transactions — like marketing).
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const overflowMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showOverflowMenu) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(t)) setShowOverflowMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [showOverflowMenu]);

  // Deep link: /stock?view=customer-orders (used by the pinned orders widget).
  // Counter sales live in the point-of-sale app; the old in-page sell view
  // predates the fulfillment engine, so that deep link goes there instead.
  useEffect(() => {
    const view = new URLSearchParams(location.search).get('view');
    if (view === 'sell') {
      navigate('/sell-products', { replace: true });
      return;
    }
    if (view && ['products', 'orders', 'customer-orders', 'history', 'metrics', 'suppliers'].includes(view)) {
      setCurrentView(view as typeof currentView);
    }
  }, [location.search]);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [selectedOrderForModal, setSelectedOrderForModal] = useState<Order | null>(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderStatusFilter, setOrderStatusFilter] = useState<'all' | 'pending' | 'received' | 'cancelled'>('pending');
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [onOrderQuantities, setOnOrderQuantities] = useState<Record<string, number>>({});
  const [expandedSales, setExpandedSales] = useState<Set<string>>(new Set());
  // History-related state
  const [transactionHistory, setTransactionHistory] = useState<TransactionHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  
  // Cart-related state for sell page
  const [cart, setCart] = useState<Array<{product: StockItem, quantity: number}>>([]);
  const [isCartOpen, setIsCartOpen] = useState(false); // Cart visibility toggle
  
  // Discount-related state
  const [discountMode, setDiscountMode] = useState<'code' | 'custom'>('code');
  const [discountCode, setDiscountCode] = useState('');
  const [customDiscountAmount, setCustomDiscountAmount] = useState('');
  const [customDiscountType, setCustomDiscountType] = useState<'%' | '$'>('%');
  const [appliedDiscount, setAppliedDiscount] = useState<{
    id?: string;
    code?: string;
    type: 'percentage' | 'fixed';
    value: number;
    description?: string;
    discountAmount: number;
    source: 'code' | 'custom';
  } | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [discountValidating, setDiscountValidating] = useState(false);
  // Server-computed cart totals (business tax rates + per-product exemption)
  const [cartQuote, setCartQuote] = useState<CounterSaleQuote | null>(null);
  const [selectedHistoryType, setSelectedHistoryType] = useState<string | null>(null);
  const [showDeletedItems] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Customer selection state
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

  // Payment modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  // Send invoice modal state
  
  // Suppliers state
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [suppliersError, setSuppliersError] = useState<string | null>(null);
  const [suppliersSearchQuery, setSuppliersSearchQuery] = useState('');
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Customer | null>(null);
  const [isEditingSupplier, setIsEditingSupplier] = useState(false);
  const [supplierEditForm, setSupplierEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [isSavingSupplier, setIsSavingSupplier] = useState(false);
  const [supplierOrders, setSupplierOrders] = useState<Order[]>([]);
  const [supplierOrdersLoading, setSupplierOrdersLoading] = useState(false);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [isSendInvoiceModalOpen, setIsSendInvoiceModalOpen] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  
  // Online Store Design Settings
  const [isDesignExpanded, setIsDesignExpanded] = useState(false);
  const [isCatalogExpanded, setIsCatalogExpanded] = useState(false);
  const [storeDesign, setStoreDesign] = useState({
    sidebarColor: '#374151', // Default gray-700
    sidebarTextColor: '#ffffff', // Default white
    headerColor: '#ffffff',  // Default white
    backgroundColor: '#f9fafb', // Default gray-50
    accentColor: '#3b82f6',   // Default blue-500
    businessNameColor: '#111827', // Default gray-900
    browseProductsColor: '#6b7280', // Default gray-500
    showBusinessLogo: true,   // Default show logo
    showBusinessName: true,   // Default show name
    showPopularProducts: false, // Default hide popular products
    popularProductIds: [] as string[],     // Default empty array
    displayStockLevels: false,   // Default hide stock levels
    hideOutOfStockProducts: false // Default show all products
  });
  
  // Shipping settings state
  const [shippingSettings, setShippingSettings] = useState({
    enableInStorePickup: true,
    pickupAddress: '',
    shippingMethods: [] as Array<{name: string, price: number}>
  });
  const [isSavingDesign, setIsSavingDesign] = useState(false);
  const [isSavingShipping, setIsSavingShipping] = useState(false);
  const [isShippingExpanded, setIsShippingExpanded] = useState(false);
  
  // Popular Products Selection
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Load available products for popular products selection
  const loadAvailableProducts = async () => {
    if (!selectedBusiness?.id) return;
    
    setIsLoadingProducts(true);
    try {
      const { data: products, error } = await supabase
        .from('stock_items')
        .select('id, name, description, price, quantity')
        .eq('business_id', selectedBusiness.id)
        .order('name');
      
      if (error) throw error;
      setAvailableProducts(products || []);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error({
        title: "Error",
        description: "Failed to load products",
        duration: 3000,
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Toggle product selection for popular products
  const toggleProductSelection = (productId: string) => {
    setStoreDesign(prev => {
      const currentIds = prev.popularProductIds || [];
      const isSelected = currentIds.includes(productId);
      
      if (isSelected) {
        // Remove product
        return {
          ...prev,
          popularProductIds: currentIds.filter(id => id !== productId)
        };
      } else if (currentIds.length < 10) {
        // Add product (max 10)
        return {
          ...prev,
          popularProductIds: [...currentIds, productId]
        };
      }
      
      return prev; // No change if already at max
    });
  };

  // Load store design settings
  const loadStoreDesignSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const { data, error } = await supabase
        .from('online_store_settings')
        .select('sidebar_color, sidebar_text_color, header_color, background_color, accent_color, business_name_color, browse_products_color, show_business_logo, show_business_name, show_popular_products, popular_product_ids, display_stock_levels, hide_out_of_stock_products, enable_in_store_pickup, pickup_address, shipping_methods')
        .eq('business_id', selectedBusiness.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No settings found, use defaults
          return;
        }
        throw error;
      }

      if (data) {
        setStoreDesign({
          sidebarColor: data.sidebar_color || '#374151',
          sidebarTextColor: data.sidebar_text_color || '#ffffff',
          headerColor: data.header_color || '#ffffff',
          backgroundColor: data.background_color || '#f9fafb',
          accentColor: data.accent_color || '#3b82f6',
          businessNameColor: data.business_name_color || '#111827',
          browseProductsColor: data.browse_products_color || '#6b7280',
          showBusinessLogo: data.show_business_logo ?? true,
          showBusinessName: data.show_business_name ?? true,
          showPopularProducts: data.show_popular_products ?? false,
          popularProductIds: data.popular_product_ids || [],
          displayStockLevels: data.display_stock_levels ?? false,
          hideOutOfStockProducts: data.hide_out_of_stock_products ?? false
        });
        
        // Load shipping settings
        setShippingSettings({
          enableInStorePickup: data.enable_in_store_pickup ?? true,
          pickupAddress: data.pickup_address || '',
          shippingMethods: data.shipping_methods || []
        });
      }
    } catch (error) {
      console.error('Error loading store design settings:', error);
      toast.error({
        title: "Error",
        description: "Failed to load store design settings",
        duration: 3000,
      });
    }
  };

  // Save store design settings
  const saveStoreDesignSettings = async () => {
    if (!selectedBusiness?.id) {
      console.error('No business selected');
      toast.error({
        title: "Error",
        description: "No business selected",
        duration: 3000,
      });
      return;
    }

    try {
      setIsSavingDesign(true);
      console.log('Saving design settings for business:', selectedBusiness.id);
      console.log('Design data:', storeDesign);

      // First, try to update existing settings
      const { data: existingData, error: selectError } = await supabase
        .from('online_store_settings')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .single();

      console.log('Existing data check:', { existingData, selectError });

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error checking existing settings:', selectError);
        throw selectError;
      }

      const settingsData = {
        business_id: selectedBusiness.id,
        sidebar_color: storeDesign.sidebarColor,
        sidebar_text_color: storeDesign.sidebarTextColor,
        header_color: storeDesign.headerColor,
        background_color: storeDesign.backgroundColor,
        accent_color: storeDesign.accentColor,
        business_name_color: storeDesign.businessNameColor,
        browse_products_color: storeDesign.browseProductsColor,
        show_business_logo: storeDesign.showBusinessLogo,
        show_business_name: storeDesign.showBusinessName,
        show_popular_products: storeDesign.showPopularProducts,
        popular_product_ids: storeDesign.popularProductIds,
        display_stock_levels: storeDesign.displayStockLevels,
        hide_out_of_stock_products: storeDesign.hideOutOfStockProducts
      };

      console.log('Settings data to save:', settingsData);

      if (existingData) {
        console.log('Updating existing settings...');
        // Update existing settings
        const { error: updateError } = await supabase
          .from('online_store_settings')
          .update(settingsData)
          .eq('business_id', selectedBusiness.id);

        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }
        console.log('Settings updated successfully');
      } else {
        console.log('Creating new settings record...');
        // Create new settings record
        const { error: insertError } = await supabase
          .from('online_store_settings')
          .insert(settingsData);

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
        console.log('Settings created successfully');
      }

      toast.success({
        title: "Success",
        description: "Design settings saved successfully!",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving store design settings:', error);
      toast.error({
        title: "Error",
        description: `Failed to save design settings: ${(error as any)?.message || 'Unknown error'}`,
        duration: 5000,
      });
    } finally {
      setIsSavingDesign(false);
    }
  };

  // Save shipping settings
  const saveShippingSettings = async () => {
    if (!selectedBusiness?.id) {
      console.error('No business selected');
      toast.error({
        title: "Error",
        description: "No business selected",
        duration: 3000,
      });
      return;
    }

    try {
      setIsSavingShipping(true);
      console.log('Saving shipping settings for business:', selectedBusiness.id);
      console.log('Shipping data:', shippingSettings);

      // First, try to update existing settings
      const { data: existingData, error: selectError } = await supabase
        .from('online_store_settings')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .single();

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error checking existing settings:', selectError);
        throw selectError;
      }

      const shippingData = {
        business_id: selectedBusiness.id,
        enable_in_store_pickup: shippingSettings.enableInStorePickup,
        pickup_address: shippingSettings.pickupAddress || null,
        shipping_methods: shippingSettings.shippingMethods
      };

      if (existingData) {
        console.log('Updating existing settings...');
        const { error: updateError } = await supabase
          .from('online_store_settings')
          .update(shippingData)
          .eq('business_id', selectedBusiness.id);

        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }
        console.log('Shipping settings updated successfully');
      } else {
        console.log('Creating new settings record...');
        const { error: insertError } = await supabase
          .from('online_store_settings')
          .insert(shippingData);

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
        console.log('Shipping settings created successfully');
      }

      toast.success({
        title: "Success",
        description: "Shipping settings saved successfully!",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving shipping settings:', error);
      toast.error({
        title: "Error",
        description: `Failed to save shipping settings: ${(error as any)?.message || 'Unknown error'}`,
        duration: 5000,
      });
    } finally {
      setIsSavingShipping(false);
    }
  };

  // Shipping methods management
  const addShippingMethod = () => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: [...prev.shippingMethods, { name: '', price: 0 }]
    }));
  };

  const updateShippingMethod = (index: number, field: 'name' | 'price', value: string | number) => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.map((method, i) => 
        i === index ? { ...method, [field]: value } : method
      )
    }));
  };

  const removeShippingMethod = (index: number) => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.filter((_, i) => i !== index)
    }));
  };

  // Cart management functions
  const addToCart = (product: StockItem) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevCart, { product, quantity: 1 }];
      }
    });
    // Don't auto-open cart - let user click cart icon to open
  };

  const updateCartQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setIsCartOpen(false);
    // Clear discount when cart is cleared
    setAppliedDiscount(null);
    setDiscountCode('');
    setCustomDiscountAmount('');
    setDiscountError(null);
    setDiscountMode('code');
  };

  // Discount management functions
  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    if (!selectedBusiness?.id) {
      setDiscountError('Business information not available');
      return;
    }

    setDiscountValidating(true);
    setDiscountError(null);

    try {
      const orderAmount = cartSubtotal;
      const result = await DiscountCodeService.validateDiscountCode(
        selectedBusiness.id, 
        discountCode, 
        orderAmount
      );
      
      if (result.isValid && result.code && result.discountAmount !== undefined) {
        const discountAmount = result.discountAmount;
        setAppliedDiscount({
          id: result.code.id,
          code: result.code.code,
          type: result.code.type,
          value: result.code.value,
          description: result.code.description,
          discountAmount: discountAmount,
          source: 'code'
        });
        setDiscountError(null);
        setDiscountCode('');
      } else {
        setAppliedDiscount(null);
        setDiscountError(result.error || 'Invalid discount code');
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
      setAppliedDiscount(null);
      setDiscountError('Error validating discount code. Please try again.');
    } finally {
      setDiscountValidating(false);
    }
  };

  const applyCustomDiscount = () => {
    const amount = parseFloat(customDiscountAmount);
    if (isNaN(amount) || amount <= 0) {
      setDiscountError('Please enter a valid discount amount');
      return;
    }

    if (customDiscountType === '%' && amount > 100) {
      setDiscountError('Percentage discount cannot exceed 100%');
      return;
    }

    // Calculate discount amount
    const discountAmount = customDiscountType === '%' 
      ? (cartSubtotal * amount / 100)
      : amount;

    setDiscountError(null);
    setAppliedDiscount({
      type: customDiscountType === '%' ? 'percentage' : 'fixed',
      value: amount,
      description: `Custom ${customDiscountType === '%' ? amount + '%' : '$' + amount} discount`,
      discountAmount: discountAmount,
      source: 'custom'
    });
    setCustomDiscountAmount('');
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountError(null);
  };

  // Send invoice functions
  const handleSendInvoice = () => {
    if (!selectedCustomer) {
      toast.error({
        title: "Customer Required",
        description: "Please add a customer before sending an invoice.",
        duration: 3000,
      });
      return;
    }
    
    if (cart.length === 0) {
      toast.error({
        title: "Cart Empty",
        description: "Please add items to cart before sending an invoice.",
        duration: 3000,
      });
      return;
    }
    
    console.log('🔍 Opening SendInvoiceModal for cart invoice...');
    setIsSendInvoiceModalOpen(true);
  };

  const handleSendInvoiceMethod = async (
    method: 'email' | 'sms',
    allowStripePay: boolean,
    _depositAmount?: number,
    saveCardOnFile?: boolean,
  ) => {
    if (method === 'email') {
      await handleSendInvoiceEmail(allowStripePay, saveCardOnFile);
    } else {
      // TODO: Implement SMS sending
      toast.error({
        title: "Not Implemented",
        description: "SMS sending not yet implemented for product invoices",
        duration: 3000,
      });
    }
  };

  const generateProductInvoicePDF = async (productSale: any): Promise<{ blob: Blob; filename: string } | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: { productSaleId: productSale.id }
      });
      if (error || !data?.base64) {
        console.error('Edge function PDF error:', error);
        return null;
      }
      const bin = atob(data.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      return { blob, filename: data.filename || `product-sale-${productSale.sale_number}.pdf` };
    } catch (error) {
      console.error('Error generating product invoice PDF:', error);
      toast.error({
        title: "PDF Generation Failed",
        description: "Failed to generate PDF. Please try again.",
        duration: 3000,
      });
      return null;
    }
  };

  const handleSendInvoiceEmail = async (allowStripePay: boolean = true, saveCardOnFile?: boolean) => {
    console.log('🚀 Starting invoice email process...');
    console.log('Selected customer:', selectedCustomer);
    console.log('Selected business:', selectedBusiness);
    console.log('Cart items:', cart);
    
    if (!selectedCustomer?.email || !selectedBusiness) {
      const errorMsg = !selectedCustomer ? 'No customer selected' : 
                      !selectedCustomer.email ? 'Customer has no email address' : 
                      'No business selected';
      toast.error({
        title: "Error",
        description: errorMsg,
        duration: 3000,
      });
      return;
    }

    if (cart.length === 0) {
      toast.error({
        title: "Cart Empty",
        description: "No items in cart to invoice",
        duration: 3000,
      });
      return;
    }

    try {
      setIsSendingInvoice(true);

      // Generate a temporary invoice number for the pending invoice
      const invoiceNumber = `INV-${Date.now()}`;

      console.log('📧 Creating professional invoice email for cart items...');

      // Create the product sale using the proper function (this will create stock transactions)
      console.log('💾 Creating product sale record...');
      
      const taxRatePercent = cartQuote?.combinedRatePercent ?? 0;
      const productSale = await createProductSale({
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        subtotal_amount: cartSubtotal,
        tax_rate: taxRatePercent,
        tax_amount: cartTaxAmount,
        total_amount: cartTotal,
        discount_amount: discountAmount > 0 ? discountAmount : undefined,
        discount_details: appliedDiscount ? [{
          id: appliedDiscount.id || 'custom',
          title: appliedDiscount.source === 'code' ? `Code: ${appliedDiscount.code}` : (appliedDiscount.description || 'Custom Discount'),
          type: appliedDiscount.type,
          amount: appliedDiscount.value
        }] : undefined,
        original_subtotal: discountAmount > 0 ? cartSubtotal + discountAmount : undefined,
        payment_status: 'unpaid', // Will be updated when payment is received
        expected_total_cents: cartQuote ? Math.round(cartQuote.total * 100) : undefined,
        notes: `Invoice ${invoiceNumber} - Products sale`,
        items: cart.map(item => ({
          stock_item_id: item.product.id,
          product_name: item.product.name,
          quantity: item.quantity,
          unit_price: item.product.price,
          tax_exempt: false, // server derives per-product exemption
          tax_rate: taxRatePercent,
          tax_amount: (item.product.price * item.quantity * taxRatePercent) / 100,
          total_price: item.product.price * item.quantity
        }))
      });

      console.log('✅ Product sale record created with stock transactions:', productSale.id, 'Sale number:', productSale.sale_number);

      // Now generate PDF with the actual sale data
      const pdfData = await generateProductInvoicePDF(productSale);
      if (!pdfData) {
        console.error('Failed to generate PDF');
        return;
      }

      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove data:application/pdf;base64, prefix
          const base64Content = base64String.split(',')[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(pdfData.blob);
      
      const base64Content = await base64Promise;

      console.log('PDF attachment prepared:', {
        filename: pdfData.filename,
        contentLength: base64Content.length,
        contentPreview: base64Content.substring(0, 50) + '...'
      });

      // Generate payment link if Stripe Connect is enabled and allowStripePay is true
      let paymentLinkHtml = '';

      if (allowStripePay && (selectedBusiness as any).stripe_connect_account_id && (selectedBusiness as any).online_payments_enabled) {
        console.log('🔍 Creating Stripe payment link for product sale...');
        
        try {
          // Create payment record linked to the product sale
          const { data: pendingPayment, error: paymentError } = await supabase
            .from('payments')
            .insert({
              business_id: selectedBusiness.id,
              booking_id: null, // NULL for product sales
              product_sale_id: productSale.id, // Direct foreign key link
              invoice_id: productSale.invoice_id ?? null, // settle the counter-sale invoice on completion
              amount: productSale.total_amount,
              method: 'credit_card',
              status: 'pending',
              reference: `Product Sale ${productSale.sale_number}`,
              notes: `Payment for product sale ${productSale.sale_number} - Product invoice`,
              payment_token: crypto.randomUUID(),
              save_card_on_file: !!(
                saveCardOnFile &&
                selectedCustomer?.id &&
                allowStripePay
              ),
              save_card_customer_id:
                saveCardOnFile && selectedCustomer?.id && allowStripePay
                  ? selectedCustomer.id
                  : null,
            })
            .select()
            .single();

          if (paymentError) {
            console.error('Failed to create payment record:', paymentError);
            console.log('⚠️ Continuing without payment link due to database constraint');
          } else {
            console.log('✅ Pending payment record created:', pendingPayment.id);

            // Generate payment link
            const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
              body: {
                paymentId: pendingPayment.id
              }
            });

            if (!linkError && linkData?.paymentUrl) {
              paymentLinkHtml = `
                <div style="margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; text-align: center;">
                  <h3 style="color: white; margin: 0 0 15px 0; font-size: 18px;">Pay Your Invoice Online</h3>
                  <p style="color: #e2e8f0; margin: 0 0 20px 0; font-size: 14px;">Secure payment with credit or debit card</p>
                  <a href="${linkData.paymentUrl}" 
                     style="display: inline-block; background: #ffffff; color: #667eea; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    Pay $${cartTotal.toFixed(2)} Now
                  </a>
                  <p style="color: #cbd5e0; margin: 15px 0 0 0; font-size: 12px;">Powered by Stripe - Secure &amp; Encrypted</p>
                </div>
              `;
              console.log('✅ Payment link generated:', linkData.paymentUrl);
            } else {
              console.error('Failed to generate payment link:', linkError);
            }
          }
        } catch (paymentLinkError) {
          console.error('Error in payment link generation:', paymentLinkError);
          console.log('⚠️ Continuing without payment link');
        }
      }

      // Prepare email content using the same professional format as booking invoices
      const customerName = `${selectedCustomer.first_name} ${selectedCustomer.last_name}`;
      const actualInvoiceNumber = productSale.sale_number || invoiceNumber;
      const emailSubject = `Invoice #${actualInvoiceNumber} from ${selectedBusiness.name}`;

      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
          <div style="padding: 40px 32px 32px 32px;">
            <div style="margin-bottom: 32px;">
              <h1 style="color: #000000; margin: 0 0 8px 0; font-size: 28px; font-weight: 600; letter-spacing: -0.02em; line-height: 1.2;">Invoice #${actualInvoiceNumber}</h1>
              <p style="color: #6b7280; margin: 0; font-size: 15px; line-height: 1.5;">${selectedBusiness.name}</p>
          </div>
          
            <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 24px 0;">Hi ${customerName},</p>
            <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 32px 0;">
              Please find attached your invoice for product sale #${actualInvoiceNumber}.
            </p>
            
            ${paymentLinkHtml}
            
            <div style="margin-top: 40px; padding-top: 24px; border-top: 1px solid #e5e7eb;">
              <p style="color: #1f2937; font-size: 15px; line-height: 1.6; margin: 0 0 8px 0;">Thank you,</p>
              <p style="color: #1f2937; font-size: 15px; line-height: 1.6; margin: 0; font-weight: 500;">${selectedBusiness?.name || 'Business'}</p>
              </div>
            </div>
            
          <div style="padding: 24px 32px; background: #f9fafb; border-top: 1px solid #e5e7eb;">
            <p style="color: #9ca3af; font-size: 13px; margin: 0; text-align: center; line-height: 1.5;">This is an automated invoice from ${selectedBusiness?.name || 'Business'}</p>
          </div>
        </div>
      `;

      // Send email using Gmail integration with PDF attachment
      const emailResult = await sendGmailEmail({
        businessId: selectedBusiness.id,
        to: selectedCustomer.email,
        subject: emailSubject,
        body: emailBody,
        attachments: [{
          filename: pdfData.filename,
          content: base64Content,
          type: 'application/pdf'
        }]
      });

      if (emailResult.success) {
        toast.success({
          title: "Invoice Sent!",
          description: "Invoice sent successfully to customer",
          duration: 4000,
        });
        console.log('✅ Product sale invoice email sent successfully');
        
        // Clear cart and close modal
        clearCart();
        setSelectedCustomer(null);
        setAppliedDiscount(null);
        setIsSendInvoiceModalOpen(false);
        
        // Refresh stock items
        loadStockItems();
      } else {
        throw new Error(emailResult.error || 'Failed to send email');
      }

    } catch (error) {
      console.error('❌ Failed to send invoice email:', error);
      toast.error({
        title: "Failed to Send",
        description: "Failed to send invoice. Please try again.",
        duration: 3000,
      });
    } finally {
      setIsSendingInvoice(false);
    }
  };

  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);
  
  // Cart totals. Tax comes from the server quote (business tax rates +
  // per-product exemption) so the displayed total matches the invoice exactly.
  const cartSubtotal = cart.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  
  // Calculate discount amount
  let discountAmount = 0;
  if (appliedDiscount) {
    // Use the pre-calculated discount amount from the discount service or custom calculation
    discountAmount = Math.min(appliedDiscount.discountAmount, cartSubtotal); // Don't exceed subtotal
  }
  
  const cartSubtotalAfterDiscount = cartSubtotal - discountAmount;
  const cartTaxAmount = cartQuote?.tax ?? 0;
  const cartTotal = cartQuote ? cartQuote.total : cartSubtotalAfterDiscount;

  // Quote the cart against the invoice engine whenever it changes
  useEffect(() => {
    if (!selectedBusiness?.id || cart.length === 0) {
      setCartQuote(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const quote = await quoteCounterSale(
          selectedBusiness.id,
          cart.map(ci => ({ stock_item_id: ci.product.id, quantity: ci.quantity, unit_price: ci.product.price })),
          toCounterSaleDiscount(appliedDiscount)
        );
        if (!cancelled) setCartQuote(quote);
      } catch (error) {
        console.error('Failed to quote cart totals:', error);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [cart, appliedDiscount, selectedBusiness?.id]);

  const loadStockItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      // Pass on-order quantities to include one-time items with items on order
      console.log('📋 Loading stock items with on-order quantities:', onOrderQuantities);
      const data = await getStockItems(selectedBusiness.id, { onOrderQuantities });
      console.log('✅ Loaded stock items:', data.length, 'items');
      const oneTimeItems = data.filter(item => item.is_one_time);
      console.log('📦 One-time items in result:', oneTimeItems.map(item => ({ name: item.name, id: item.id, quantity: item.quantity, onOrder: onOrderQuantities[item.id] || 0 })));
      setItems(data);
      setError(null);

      // Load product groups (for variant grouping in the inventory grid)
      try {
        const groups = await getProductGroupsWithImages(selectedBusiness.id);
        setProductGroups(groups);
      } catch (groupErr) {
        console.error('Error loading product groups:', groupErr);
      }
      
      // Load photos for all items
      await loadProductPhotos(data.map(item => item.id));
    } catch (err) {
      console.error('Error loading stock items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load stock items');
    } finally {
      setLoading(false);
    }
  };

  const loadOnOrderQuantities = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const quantities = await getOnOrderQuantities(selectedBusiness.id);
      console.log('📦 Loaded on-order quantities:', quantities);
      setOnOrderQuantities(quantities);
      // Note: Stock items will be reloaded via useEffect when onOrderQuantities changes
    } catch (err) {
      console.error('Error loading on order quantities:', err);
    }
  };

  // Handle real-time stock updates
  const handleStockUpdate = useCallback((payload: RealtimePostgresChangesPayload<StockItem>) => {
    console.log('📡 Real-time stock update received:', payload);
    console.log('📡 Event type:', payload.eventType);
    console.log('📡 Table:', payload.table);
    console.log('📡 New data:', payload.new);
    console.log('📡 Old data:', payload.old);
    
    if (payload.eventType === 'UPDATE') {
      const updatedItem = payload.new as StockItem;
      console.log('🔄 Updating stock item:', updatedItem.name, 'Old quantity vs New quantity');
      console.log('🔄 New quantity:', updatedItem.quantity);
      
      setItems(prevItems => {
        const updated = prevItems.map(item => {
          if (item.id === updatedItem.id) {
            console.log(`🔄 Item ${item.name}: ${item.quantity} → ${updatedItem.quantity}`);
            return { ...item, ...updatedItem };
          }
          return item;
        });
        return updated;
      });
    } else if (payload.eventType === 'INSERT') {
      const newItem = payload.new as StockItem;
      console.log('➕ New stock item added:', newItem.name);
      
      setItems(prevItems => [...prevItems, newItem]);
    } else if (payload.eventType === 'DELETE') {
      const deletedItem = payload.old as StockItem;
      console.log('🗑️ Stock item deleted:', deletedItem.name);
      
      setItems(prevItems => prevItems.filter(item => item.id !== deletedItem.id));
    }
  }, []);

  // Set up real-time subscription
  useEffect(() => {
    if (!selectedBusiness?.id) return;

    console.log('🔌 Setting up real-time subscription for stock items...');
    
    const channel = supabase
      .channel('stock_items_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stock_items',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        handleStockUpdate
      )
      .subscribe((status) => {
        console.log('📡 Stock items subscription status:', status);
      });

    return () => {
      console.log('🔌 Cleaning up stock items subscription...');
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id, handleStockUpdate]);

  const loadProductPhotos = async (stockItemIds: string[]) => {
    if (stockItemIds.length === 0) return;

    try {
      const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
      
      // Convert to the format expected by the component
      const photoUrls: Record<string, string> = {};
      Object.entries(photoMap).forEach(([itemId, photo]) => {
        photoUrls[itemId] = photo.photo_url;
      });
      
      setProductPhotos(photoUrls);
    } catch (err) {
      console.error('Error loading product photos:', err);
      // Don't show error to user for photos, just log it
    }
  };

  const loadHistory = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setHistoryLoading(true);
      // Load stock history (not used in UI anymore, but kept for compatibility)
      await getStockHistory(selectedBusiness.id);
      setHistoryError(null);
    } catch (err) {
      console.error('Error loading history:', err);
      setHistoryError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadTransactionHistory = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setHistoryLoading(true);
      const transactionData = await getTransactionHistory(selectedBusiness.id);
      setTransactionHistory(transactionData);
      setHistoryError(null);
    } catch (err) {
      console.error('Error loading transaction history:', err);
      setHistoryError(err instanceof Error ? err.message : 'Failed to load transaction history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadSuppliers = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setSuppliersLoading(true);
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
      setSuppliersError(null);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setSuppliersError(err instanceof Error ? err.message : 'Failed to load suppliers');
    } finally {
      setSuppliersLoading(false);
    }
  };

  // Load recent orders from the database
  const loadRecentOrders = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoadingOrders(true);
      const orders = await getOrders(selectedBusiness.id);
      setRecentOrders(orders);
    } catch (err) {
      console.error('Error loading recent orders:', err);
      toast.error({
        title: "Error",
        description: "Failed to load recent orders",
        duration: 3000,
      });
    } finally {
      setLoadingOrders(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (selectedBusiness?.id) {
      // Load on-order quantities first, then stock items (so one-time items with orders are included)
      loadOnOrderQuantities();
      loadRecentOrders();
      loadStoreDesignSettings();
    }
  }, [selectedBusiness?.id, loadRecentOrders]);

  // Keep PO detail modal in sync when orders refresh (e.g. after receive)
  useEffect(() => {
    if (!selectedOrderForModal?.id || recentOrders.length === 0) return;
    const updated = recentOrders.find(o => o.id === selectedOrderForModal.id);
    if (updated) setSelectedOrderForModal(updated);
    else setSelectedOrderForModal(null); // order was deleted
  }, [recentOrders, selectedOrderForModal?.id]);

  // Reload stock items when onOrderQuantities changes (to include one-time items with orders)
  useEffect(() => {
    if (selectedBusiness?.id) {
      console.log('🔄 onOrderQuantities changed, reloading stock items...', onOrderQuantities);
      loadStockItems();
    }
  }, [selectedBusiness?.id, onOrderQuantities]);

  // Load history when switching to history view
  useEffect(() => {
    if (currentView === 'history') {
      loadTransactionHistory();
    }
  }, [currentView, selectedBusiness?.id]);

  // Load supplier orders when supplier is selected
  const loadSupplierOrders = useCallback(async () => {
    if (!selectedSupplier?.id || !selectedBusiness?.id) {
      setSupplierOrders([]);
      return;
    }

    setSupplierOrdersLoading(true);
    try {
      const orders = await getOrders(selectedBusiness.id);
      // Filter orders for the selected supplier
      const filteredOrders = orders.filter(order => order.supplier_id === selectedSupplier.id);
      setSupplierOrders(filteredOrders);
    } catch (err) {
      console.error('Error loading supplier orders:', err);
      toast.error({ title: 'Error', description: 'Failed to load orders' });
    } finally {
      setSupplierOrdersLoading(false);
    }
  }, [selectedSupplier?.id, selectedBusiness?.id]);

  // Load suppliers when switching to suppliers view
  useEffect(() => {
    if (currentView === 'suppliers') {
      loadSuppliers();
    }
  }, [currentView, selectedBusiness?.id]);

  // Reset edit form when supplier changes
  useEffect(() => {
    if (selectedSupplier) {
      setIsEditingSupplier(false);
      setSupplierEditForm({
        first_name: selectedSupplier.first_name || '',
        last_name: selectedSupplier.last_name || '',
        email: selectedSupplier.email || '',
        phone: selectedSupplier.phone || '',
        address: selectedSupplier.address || ''
      });
    }
  }, [selectedSupplier?.id]);

  // Load supplier orders when supplier is selected
  useEffect(() => {
    loadSupplierOrders();
  }, [loadSupplierOrders]);

  // Load products when popular products is enabled
  useEffect(() => {
    if (storeDesign.showPopularProducts && availableProducts.length === 0) {
      loadAvailableProducts();
    }
  }, [storeDesign.showPopularProducts]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isCategoryDropdownOpen) {
        const target = event.target as Element;
        if (!target.closest('.category-dropdown')) {
          setIsCategoryDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCategoryDropdownOpen]);

  // Group transactions by sale number
  const groupTransactionsBySale = (transactions: TransactionHistoryEntry[]) => {
    const grouped: { [key: string]: TransactionHistoryEntry[] } = {};
    const ungrouped: TransactionHistoryEntry[] = [];

    transactions.forEach(entry => {
      if (entry.transaction_type === 'Stock Transaction' && entry.sale_number) {
        if (!grouped[entry.sale_number]) {
          grouped[entry.sale_number] = [];
        }
        grouped[entry.sale_number].push(entry);
    } else {
        ungrouped.push(entry);
      }
    });

    // Create grouped entries
    const groupedEntries: TransactionHistoryEntry[] = [];
    
    Object.entries(grouped).forEach(([saleNumber, items]) => {
      // Create a grouped entry for ALL sales with sale numbers (single or multiple items)
      const firstItem = items[0];
      const totalItemsSold = items.reduce((sum, item) => sum + Math.abs(item.quantity_change || 0), 0);
      
      const groupedEntry: TransactionHistoryEntry = {
        id: `grouped-${saleNumber}`,
        transaction_type: 'Grouped Sale',
        type: 'Sale',
        created_at: firstItem.created_at,
        created_by_email: firstItem.created_by_email,
        sale_number: saleNumber,
        customer_name: firstItem.customer_name,
        customer_email: firstItem.customer_email,
        total_amount: firstItem.total_amount,
        payment_status: firstItem.payment_status,
        total_items_sold: totalItemsSold,
        related_transactions: items,
        is_grouped: true
      };
      groupedEntries.push(groupedEntry);
    });

    // Combine grouped and ungrouped entries
    return [...groupedEntries, ...ungrouped].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  };

  // Filter transaction history based on search query, type, and deleted status
  const filteredTransactionHistory = transactionHistory.filter(entry => {
    const searchLower = historySearchQuery.toLowerCase();
    
    let matchesSearch = false;
    if (entry.transaction_type === 'Stock Transaction') {
      matchesSearch = 
        (entry.item_name?.toLowerCase().includes(searchLower) || false) ||
        (entry.category_name?.toLowerCase().includes(searchLower) || false) ||
        (entry.notes?.toLowerCase().includes(searchLower) || false) ||
        (entry.created_by_email?.toLowerCase().includes(searchLower) || false);
    } else if (entry.transaction_type === 'Product Sale') {
      matchesSearch = 
        (entry.sale_number?.toLowerCase().includes(searchLower) || false) ||
        (entry.customer_name?.toLowerCase().includes(searchLower) || false) ||
        (entry.customer_email?.toLowerCase().includes(searchLower) || false) ||
        (entry.created_by_email?.toLowerCase().includes(searchLower) || false);
    }

    const matchesType = !selectedHistoryType || 
      (selectedHistoryType === 'Stock Transaction' && entry.transaction_type === 'Stock Transaction') ||
      (selectedHistoryType === 'Product Sale' && (entry.transaction_type === 'Product Sale' || entry.transaction_type === 'Grouped Sale')) ||
      (selectedHistoryType === entry.type); // For specific types like 'Received', 'Withdrawn', etc.
    
    // Filter by deleted status if necessary (only applies to stock transactions)
    const matchesDeletedStatus = entry.transaction_type === 'Product Sale' || 
      entry.transaction_type === 'Grouped Sale' ||
      showDeletedItems || !(entry.is_item_deleted || false);

    return matchesSearch && matchesType && matchesDeletedStatus;
  });

  // Apply grouping to filtered results
  const groupedTransactionHistory = groupTransactionsBySale(filteredTransactionHistory);

  // Function to toggle grouped sale expansion
  const toggleSaleExpansion = (saleNumber: string) => {
    setExpandedSales(prev => {
      const next = new Set(prev);
      if (next.has(saleNumber)) {
        next.delete(saleNumber);
      } else {
        next.add(saleNumber);
      }
      return next;
    });
  };

  const handleDeleteHistoryEntry = async (entry: StockHistoryEntry) => {
    if (!entry) return;
    
    try {
      setDeleteLoading(true);
      setHistoryError(null);

      // Get current stock quantity first
      const { data: stockItem, error: stockError } = await supabase
        .from('stock_items')
        .select('quantity')
        .eq('id', entry.item_id)
        .single();

      if (stockError && !stockError.message.includes('no rows returned')) {
        throw stockError;
      }

      // Item might be deleted, so we need to handle that case
      if (stockError && stockError.message.includes('no rows returned')) {
        // For deleted items, we can still delete history entries
        try {
          await deleteStockHistoryEntry(entry.id);
          // Refresh transaction history after deletion
          await loadTransactionHistory();
          // Silent refresh of products tab to update stock quantities
          loadStockItems().then(() => {
            console.log('✅ Stock items refreshed after deleted item entry deletion');
          }).catch(error => {
            console.error('❌ Error refreshing stock items after deletion:', error);
          });
        } catch (err) {
          console.error('Error deleting entry:', err);
          setHistoryError(err instanceof Error ? err.message : 'Failed to delete entry');
          return;
        }
      } else {
        // For active items, check quantities (untracked items move no stock)
        if (entry.transaction_type === 'Received' && 
            stockItem && stockItem.track_inventory !== false &&
            stockItem.quantity < entry.quantity_change) {
          const proceed = window.confirm(
            `Deleting this entry will set the item quantity to 0 since there is not enough stock. Continue?`
          );
          
          if (!proceed) {
            setDeleteLoading(false);
            return;
          }
        }

        try {
          await deleteStockHistoryEntry(entry.id);
          // Refresh transaction history after deletion
          await loadTransactionHistory();
          // Silent refresh of products tab to update stock quantities
          loadStockItems().then(() => {
            console.log('✅ Stock items refreshed after history entry deletion');
          }).catch(error => {
            console.error('❌ Error refreshing stock items after deletion:', error);
          });
        } catch (err) {
          // If deletion failed, show error but keep modal open
          console.error('Error deleting entry:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to delete entry';
          if (errorMessage.includes('negative stock quantity')) {
            setHistoryError('This entry cannot be deleted as it would result in invalid stock levels.');
          } else {
            setHistoryError('Failed to delete entry. Please try again.');
          }
          return;
        }
      }
    } catch (err) {
      console.error('Error deleting history entry:', err);
      setHistoryError('Failed to load current stock levels. Please try again.');
      return;
    } finally {
      setDeleteLoading(false);
    }
  };



  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    // Filter items first (same as allItems) before grouping
    const filteredItems = items.filter(item => {
      if (item.is_deleted) return false;
      if (item.is_one_time && item.quantity === 0) return false;
      return true;
    });
    
    const grouped = filteredItems.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: StockItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  // Get all items (filtered by search and category)
  // Filter out deleted items AND archived one-time items
  const allItems = items.filter(item => {
    // Always exclude deleted items
    if (item.is_deleted) return false;
    
    // Exclude one-time items that are archived (quantity = 0 and is_one_time = true)
    // UNLESS they have items on order
    if (item.is_one_time && item.quantity === 0) {
      const onOrderQty = onOrderQuantities[item.id] || 0;
      if (onOrderQty > 0) {
        // Include if it has items on order
        return true;
      }
      return false;
    }
    
    return true;
  });
  
  // Filter items based on search and category
  const filteredItems = allItems.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || 
      (item.category?.name || 'Uncategorized') === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Build grouped inventory entries: variant items collapse under their product
  // group; standalone items render individually. Order follows first appearance.
  const inventoryEntries = React.useMemo<InventoryEntry[]>(() => {
    const groupById = new Map(productGroups.map(g => [g.id, g]));
    const variantsByGroup = new Map<string, StockItem[]>();
    const entries: InventoryEntry[] = [];
    const seen = new Set<string>();
    for (const item of filteredItems) {
      const gid = item.product_group_id || null;
      if (gid && groupById.has(gid)) {
        if (!variantsByGroup.has(gid)) variantsByGroup.set(gid, []);
        variantsByGroup.get(gid)!.push(item);
        if (!seen.has(gid)) {
          seen.add(gid);
          entries.push({ type: 'group', group: groupById.get(gid)!, variants: [] });
        }
      } else {
        entries.push({ type: 'item', item });
      }
    }
    return entries.map(e =>
      e.type === 'group' ? { ...e, variants: variantsByGroup.get(e.group.id) || [] } : e
    );
  }, [filteredItems, productGroups]);

  // Get categories with item counts (using allItems to exclude one-time items with 0 quantity)
  const categories = itemsByCategory.map(category => {
    // Filter items the same way as allItems
    const filteredCategoryItems = category.items.filter(item => {
      if (item.is_deleted) return false;
      if (item.is_one_time && item.quantity === 0) {
        const onOrderQty = onOrderQuantities[item.id] || 0;
        if (onOrderQty > 0) {
          // Include if it has items on order
          return true;
        }
        return false;
      }
      return true;
    });
    
    return {
      id: category.id,
      name: category.name,
      item_count: filteredCategoryItems.length
    };
  }).filter(category => category.item_count > 0);

  // Render product card - iOS Style
  const renderProductCard = (item: StockItem) => {
    const tracksInventory = item.track_inventory !== false;
    const isLowStock = tracksInventory && item.reorder_point != null && item.quantity <= item.reorder_point;
    const hasPhoto = productPhotos[item.id];
    const onOrderQuantity = onOrderQuantities[item.id] || 0;
    
    return (
      <div key={item.id} 
           onClick={() => {
             setEditingItem(item);
           }}
           className="bg-white rounded-2xl overflow-hidden shadow-sm active:shadow-md transition-all duration-200 cursor-pointer border border-gray-200/50 touch-manipulation"
           style={{ WebkitTapHighlightColor: 'transparent' }}>
        
        {/* Square Image Container - Fills top of card */}
        <div className="relative aspect-square bg-gray-50 overflow-hidden">
          {/* Low Stock Alert - Top Left Corner */}
          {isLowStock && (
            <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-red-500 text-white rounded-lg shadow-md z-10">
              <AlertCircle className="h-3 w-3" />
              <span className="text-[10px] font-semibold">Low</span>
            </div>
          )}

          {/* On Order Badge - Top Right Corner */}
          {onOrderQuantity > 0 && (
            <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded-lg shadow-md z-10">
              <ShoppingCart className="h-3 w-3" />
              <span className="text-[10px] font-bold">{onOrderQuantity}</span>
            </div>
          )}

          {/* One-Time Item Badge - Bottom Right Corner */}
          {item.is_one_time && (
            <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 bg-orange-500 text-white rounded-lg shadow-md z-10">
              <Clock className="h-3 w-3" />
              <span className="text-[10px] font-semibold">One-Time</span>
            </div>
          )}

          {/* Rental Badge - Bottom Left Corner */}
          {item.rental_enabled && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded-lg shadow-md z-10">
              <CalendarClock className="h-3 w-3" />
              <span className="text-[10px] font-semibold">Rental</span>
            </div>
          )}

          {/* Product Photo - Fills entire square */}
          {hasPhoto ? (
            <img 
              src={hasPhoto} 
              alt={item.name}
              className="w-full h-full object-contain p-2 transition-transform duration-200"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package2 className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300" />
            </div>
          )}
        </div>

        {/* Info Footer - iOS Style */}
        <div className="p-3 sm:p-4 bg-white">
          <h3 className="text-[13px] sm:text-sm font-semibold text-gray-900 mb-2 line-clamp-2 leading-tight">
            {item.name}
          </h3>
          
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              {tracksInventory ? (
                <div className="flex items-baseline gap-1">
                  <span className={`text-lg sm:text-xl font-bold ${isLowStock ? 'text-red-600' : 'text-gray-900'}`}>
                    {item.quantity}
                  </span>
                  <span className="text-[11px] sm:text-xs text-gray-500">{item.unit}</span>
                </div>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-[10px] sm:text-xs font-semibold text-gray-500 w-fit">
                  Made to order
                </span>
              )}
              {onOrderQuantity > 0 && (
                <span className="text-[10px] sm:text-xs text-blue-600 font-semibold">
                  On Order: {onOrderQuantity} {item.unit}
                </span>
              )}
            </div>
            
            {item.sku && (
              <span className="text-[10px] sm:text-xs text-gray-400 font-mono">#{item.sku}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderProductGroupCard = (group: ProductGroupWithImages, variants: StockItem[]) => {
    const image = group.images?.[0]?.url || variants.find(v => v.image_url)?.image_url;
    const totalQty = variants.reduce((s, v) => s + (v.quantity || 0), 0);
    const prices = variants.map(v => v.price).filter(p => typeof p === 'number') as number[];
    const minP = prices.length ? Math.min(...prices) : 0;
    const maxP = prices.length ? Math.max(...prices) : 0;
    const priceLabel = prices.length
      ? minP === maxP
        ? `$${minP.toFixed(2)}`
        : `$${minP.toFixed(2)}–$${maxP.toFixed(2)}`
      : '';
    return (
      <div key={`group-${group.id}`}
           onClick={() => setViewingGroup({ group, variants })}
           className="bg-white rounded-2xl overflow-hidden shadow-sm active:shadow-md transition-all duration-200 cursor-pointer border border-gray-200/50 touch-manipulation"
           style={{ WebkitTapHighlightColor: 'transparent' }}>
        <div className="relative aspect-square bg-gray-50 overflow-hidden">
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-gray-900/80 text-white rounded-lg shadow-md z-10">
            <Layers className="h-3 w-3" />
            <span className="text-[10px] font-semibold">{variants.length}</span>
          </div>
          {variants.some(v => v.rental_enabled) && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded-lg shadow-md z-10">
              <CalendarClock className="h-3 w-3" />
              <span className="text-[10px] font-semibold">Rental</span>
            </div>
          )}
          {image ? (
            <img src={image} alt={group.title} className="w-full h-full object-contain p-2" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package2 className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300" />
            </div>
          )}
        </div>
        <div className="p-3 sm:p-4 bg-white">
          <h3 className="text-[13px] sm:text-sm font-semibold text-gray-900 mb-2 line-clamp-2 leading-tight">{group.title}</h3>
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-1">
              <span className="text-lg sm:text-xl font-bold text-gray-900">{totalQty}</span>
              <span className="text-[11px] sm:text-xs text-gray-500">in stock</span>
            </div>
            {priceLabel && <span className="text-[11px] sm:text-xs font-semibold text-gray-700">{priceLabel}</span>}
          </div>
        </div>
      </div>
    );
  };



  // Format date function
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <PermissionGuard permission="supplies.pageAccess">
      <div className="h-[calc(100vh-3rem)] bg-gray-50"
           data-stock-page
           style={{
             touchAction: 'pan-y',
             WebkitTouchCallout: 'none',
             WebkitUserSelect: 'none',
             fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
           }}>
        <div className="flex flex-col h-full">
          {/* Header — title + ⋯ overflow menu (Rentals / Suppliers).
              Transactions lives on the bottom bar. */}
          <div className="border-b border-gray-200/60 bg-white sticky top-0 z-10 flex-shrink-0">
            <div className="flex items-center justify-between px-2.5 sm:px-4 h-12">
              <div className="flex items-center gap-1 min-w-0">
                {currentView !== 'products' ? (
                  <button
                    type="button"
                    onClick={() => setCurrentView('products')}
                    aria-label="Back to inventory"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                ) : null}
                <h1
                  className="text-[17px] font-bold text-gray-900 truncate pl-1"
                  style={{ letterSpacing: '-0.02em' }}
                >
                  {{
                    products: 'Inventory',
                    'customer-orders': 'Orders',
                    rentals: 'Rentals',
                    orders: 'Purchase Orders',
                    suppliers: 'Suppliers',
                    history: 'Transactions',
                    sell: 'Sell',
                    metrics: 'Metrics',
                  }[currentView]}
                </h1>
              </div>

              <div className="relative" ref={overflowMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowOverflowMenu((v) => !v)}
                  aria-label="Inventory menu"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
                {showOverflowMenu ? (
                  <div className="absolute right-0 top-full mt-1.5 w-52 rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden py-1 z-30">
                    {(
                      [
                        ...(items.some(i => i.rental_enabled)
                          ? [{ id: 'rentals' as const, label: 'Rentals' }]
                          : []),
                        { id: 'suppliers' as const, label: 'Suppliers' },
                      ] as { id: 'rentals' | 'suppliers'; label: string }[]
                    ).map((item) => {
                      const isActive = currentView === item.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => {
                            setShowOverflowMenu(false);
                            setCurrentView(item.id);
                          }}
                          className={`flex w-full items-center px-4 py-2.5 text-left text-[14px] touch-manipulation ${
                            isActive
                              ? 'font-semibold text-gray-900 bg-gray-50'
                              : 'font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          {currentView === 'sell' ? (
            // Sell view - full width
            <div className="flex-1 h-full flex">
              {/* Main Products Area */}
              <div className="flex-1 flex flex-col">
                {/* Header */}
                <div className="bg-green-600 text-white p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <ShoppingCart className="h-6 w-6" />
                    <div>
                      <h2 className="text-lg font-semibold">Sell Products</h2>
                      <p className="text-green-100 text-sm">Select products to sell</p>
                    </div>
                  </div>
                  {cart.length > 0 && (
                    <button
                      onClick={() => setIsCartOpen(!isCartOpen)}
                      className="flex items-center space-x-2 bg-green-700 hover:bg-green-800 px-3 py-1 rounded-lg transition-colors"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      <span className="font-medium">{cartItemCount} items</span>
                      <span className="font-bold">${cartTotal.toFixed(2)}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isCartOpen ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>

                {/* Search Bar */}
                <div className="p-4 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>

                {/* Category Tabs */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                        selectedCategory === 'all'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                      }`}
                    >
                      All Products ({allItems.length})
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.name)}
                        className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                          selectedCategory === category.name
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                        }`}
                      >
                        {category.name} ({category.item_count})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Products Grid */}
                <div className="flex-1 overflow-y-auto p-4">
                  {filteredItems.length === 0 ? (
                    <div className="h-60 flex items-center justify-center">
                      <div className="text-center">
                        <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h4>
                        <p className="text-gray-500">
                          {searchQuery || selectedCategory !== 'all' 
                            ? 'Try adjusting your search or category filter.'
                            : 'Add some inventory items to start selling.'
                          }
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3">
                      {filteredItems.map((item) => {
                        const cartItem = cart.find(cartItem => cartItem.product.id === item.id);
                        return (
                          <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                            {/* Product Image */}
                            <div className="aspect-square bg-gray-100 rounded-lg mb-2 flex items-center justify-center overflow-hidden">
                              {productPhotos[item.id] ? (
                                <img
                                  src={productPhotos[item.id]}
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <Package2 className="h-6 w-6 text-gray-400" />
                              )}
                            </div>
                            
                            {/* Product Info */}
                            <div className="space-y-1.5">
                              <h3 className="font-medium text-gray-900 text-xs line-clamp-2">{item.name}</h3>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-gray-900">${item.price?.toFixed(2) || '0.00'}</span>
                                <span className="text-xs text-gray-500">{item.quantity} {item.unit}</span>
                              </div>
                              
                              {/* Category Tag */}
                              {item.category && (
                                <div className="flex items-center gap-1">
                                  <Tag className="h-2.5 w-2.5 text-purple-600" />
                                  <span className="text-xs text-purple-700 font-medium truncate">{item.category.name}</span>
                                </div>
                              )}
                              
                              {/* Add to Cart / Quantity Controls */}
                              {cartItem ? (
                                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md p-1.5">
                                  <button
                                    onClick={() => updateCartQuantity(item.id, cartItem.quantity - 1)}
                                    className="w-6 h-6 bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center justify-center transition-colors"
                                  >
                                    <Minus className="h-3 w-3" />
                                  </button>
                                  <span className="font-medium text-green-900 text-sm">{cartItem.quantity}</span>
                                  <button
                                    onClick={() => updateCartQuantity(item.id, cartItem.quantity + 1)}
                                    className="w-6 h-6 bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center justify-center transition-colors"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => addToCart(item)}
                                  className="w-full bg-green-600 hover:bg-green-700 text-white py-1.5 px-2 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add to Cart
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Cart Modal */}
              {cart.length > 0 && isCartOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-20 flex items-center justify-center p-4">
                  <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                  {/* Cart Header */}
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Shopping Cart</h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsCartOpen(false)}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <X className="h-5 w-5 text-gray-500" />
                      </button>
                      <button
                        onClick={clearCart}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Clear Cart"
                      >
                        <Trash2 className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>
                  </div>

                  {/* Cart Items */}
                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-3">
                      {cart.map((cartItem) => (
                        <div key={cartItem.product.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                            {productPhotos[cartItem.product.id] ? (
                              <img
                                src={productPhotos[cartItem.product.id]}
                                alt={cartItem.product.name}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <Package2 className="h-6 w-6 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 text-sm truncate">{cartItem.product.name}</h4>
                            <p className="text-sm text-gray-600">${cartItem.product.price?.toFixed(2) || '0.00'} each</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateCartQuantity(cartItem.product.id, cartItem.quantity - 1)}
                              className="w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center transition-colors"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="font-medium text-gray-900 w-8 text-center">{cartItem.quantity}</span>
                            <button
                              onClick={() => updateCartQuantity(cartItem.product.id, cartItem.quantity + 1)}
                              className="w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center transition-colors"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cart Footer */}
                  <div className="border-t border-gray-200 p-4 space-y-4">
                    {/* Apply Discount Section */}
                    <div className="space-y-3">
                      {/* Show discount input only when no discount is applied */}
                      {!appliedDiscount && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Apply Discount</span>
                            <div className="flex bg-gray-100 rounded-lg p-1">
                              <button
                                onClick={() => {
                                  setDiscountMode('code');
                                  setDiscountError('');
                                }}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                  discountMode === 'code'
                                    ? 'bg-green-600 text-white'
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                              >
                                Code
                              </button>
                              <button
                                onClick={() => {
                                  setDiscountMode('custom');
                                  setDiscountError('');
                                }}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                  discountMode === 'custom'
                                    ? 'bg-green-600 text-white'
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                              >
                                Custom
                              </button>
                            </div>
                          </div>

                          {/* Discount Code Input */}
                          {discountMode === 'code' && (
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="Enter discount code"
                                value={discountCode}
                                onChange={(e) => setDiscountCode(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && !discountValidating && applyDiscountCode()}
                                disabled={discountValidating}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                              />
                              <button
                                onClick={applyDiscountCode}
                                disabled={discountValidating}
                                className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                              >
                                {discountValidating ? 'Validating...' : 'Apply'}
                              </button>
                            </div>
                          )}

                          {/* Custom Discount Input */}
                          {discountMode === 'custom' && (
                            <div className="flex gap-2">
                              <input
                                type="number"
                                placeholder="Amount"
                                value={customDiscountAmount}
                                onChange={(e) => setCustomDiscountAmount(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              />
                              <select
                                value={customDiscountType}
                                onChange={(e) => setCustomDiscountType(e.target.value as '%' | '$')}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              >
                                <option value="%">%</option>
                                <option value="$">$</option>
                              </select>
                              <button
                                onClick={applyCustomDiscount}
                                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                              >
                                Apply
                              </button>
                            </div>
                          )}

                          {/* Discount Error */}
                          {discountError && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <div className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-red-600" />
                                <span className="text-sm text-red-700">{discountError}</span>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Applied Discount Display - Only show when discount is applied */}
                      {appliedDiscount && (
                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-green-900">
                              {appliedDiscount.source === 'code' ? `Code: ${appliedDiscount.code}` : appliedDiscount.description}
                            </span>
                          </div>
                          <button
                            onClick={removeDiscount}
                            className="text-green-600 hover:text-green-800 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Add Customer Section */}
                    <div className="space-y-3">
                      {selectedCustomer ? (
                        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {selectedCustomer.first_name?.[0]}{selectedCustomer.last_name?.[0]}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-blue-900">
                                {selectedCustomer.first_name} {selectedCustomer.last_name}
                              </p>
                              {selectedCustomer.email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="h-3 w-3 text-blue-600" />
                                  <p className="text-xs text-blue-700">{selectedCustomer.email}</p>
                                </div>
                              )}
                              {selectedCustomer.phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="h-3 w-3 text-blue-600" />
                                  <p className="text-xs text-blue-700">{selectedCustomer.phone}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedCustomer(null)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setIsCustomerModalOpen(true)}
                          className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                          Add Customer
                        </button>
                      )}
                    </div>

                    {/* Totals */}
                    <div className="space-y-2 pt-3 border-t border-gray-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">${cartSubtotal.toFixed(2)}</span>
                      </div>
                      {appliedDiscount && (
                        <div className="flex justify-between text-sm">
                          <span className="text-green-600">Discount:</span>
                          <span className="font-medium text-green-600">-${discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tax (5%):</span>
                        <span className="font-medium">${cartTaxAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                        <span>Total:</span>
                        <span>${cartTotal.toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleSendInvoice}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.83 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Send Invoice
                      </button>
                      <button
                        onClick={() => setIsPaymentModalOpen(true)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <CreditCard className="h-4 w-4" />
                        Take Payment
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              )}

            </div>
          ) : (
            // Regular views - full width
            <div className="flex-1 flex flex-col overflow-hidden">
              
              {/* Category Tabs & Inventory Grid */}
            <div className="bg-white border-0 flex flex-col h-full">
              
              {/* Results — New item / Receive / Withdraw live in the bottom nav's [+] menu */}
              <div className={`flex-1 overflow-y-auto ${currentView === 'suppliers' ? '' : 'p-4 sm:p-6'} pb-24`}>
                {currentView === 'products' ? (
                  <>
                    {/* Search + category filter + count row (mockup order:
                        search · All ▾ · Count N items) — always visible */}
                    <div className="mb-6 flex items-center gap-2.5 sm:gap-3">
                      {/* Search Bar — left, grows */}
                      <div className="relative flex-1 min-w-0">
                        <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-[18px] w-[18px] text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search inventory..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-xl text-[15px] text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>

                      {/* Category Dropdown — compact "All ▾" */}
                      <div className="relative category-dropdown flex-none">
                        <button
                          onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                          className="flex items-center gap-1.5 px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors whitespace-nowrap"
                        >
                          <span className="max-w-[110px] truncate">
                            {selectedCategory === 'all' ? 'All' : selectedCategory}
                          </span>
                          <ChevronDown className={`h-4 w-4 flex-none transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isCategoryDropdownOpen && (
                          <div className="absolute top-full right-0 mt-1 min-w-[200px] bg-white border border-gray-200 rounded-xl shadow-lg z-10">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  setSelectedCategory('all');
                                  setIsCategoryDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                  selectedCategory === 'all' 
                                    ? 'bg-blue-50 text-blue-600 font-medium' 
                                    : 'text-gray-700'
                                }`}
                              >
                                All Items ({allItems.length})
                              </button>
                              {categories.map((category) => (
                                <button
                                  key={category.id}
                                  onClick={() => {
                                    setSelectedCategory(category.name);
                                    setIsCategoryDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                    selectedCategory === category.name
                                      ? 'bg-blue-50 text-blue-600 font-medium'
                                      : 'text-gray-700'
                                  }`}
                                >
                                  {category.name} ({category.item_count})
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Results Count — right */}
                      <p className="text-xs sm:text-sm text-gray-600 whitespace-nowrap flex-none">
                        Count {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
                      </p>
                    </div>

                    {/* Content Area */}
                    {loading ? (
                      <div className="h-60 flex items-center justify-center">
                        <div className="text-center">
                          <Package2 className="h-8 w-8 text-purple-500 mx-auto mb-4 animate-pulse" />
                          <p className="text-gray-600">Loading inventory...</p>
                        </div>
                      </div>
                    ) : error ? (
                      <div className="h-60 flex items-center justify-center">
                        <div className="text-center">
                          <Package2 className="h-8 w-8 text-red-500 mx-auto mb-4" />
                          <p className="text-red-600">{error}</p>
                        </div>
                      </div>
                    ) : filteredItems.length === 0 ? (
                      <div className="h-60 flex items-center justify-center">
                        <div className="text-center">
                          <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h4 className="text-lg font-medium text-gray-900 mb-2">
                            {searchQuery || selectedCategory !== 'all' ? 'No Results Found' : 'No Inventory Items'}
                          </h4>
                          <p className="text-gray-500 max-w-sm mx-auto mb-6">
                            {searchQuery || selectedCategory !== 'all' 
                              ? 'Try adjusting your search or category filter.'
                              : 'Get started by adding your first inventory item to track supplies and manage stock levels.'
                            }
                          </p>
                          {(!searchQuery && selectedCategory === 'all') && (
                            <button
                              onClick={() => setIsStockModalOpen(true)}
                              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                            >
                              <Plus className="h-4 w-4 mr-2 inline" />
                              Add First Item
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3 sm:gap-4">
                        {inventoryEntries.map(entry =>
                          entry.type === 'group'
                            ? renderProductGroupCard(entry.group, entry.variants)
                            : renderProductCard(entry.item)
                        )}
                      </div>
                    )}
                  </>
                ) : currentView === 'customer-orders' ? (
                  selectedBusiness && (
                    <CustomerOrdersTab
                      businessId={selectedBusiness.id}
                      onInvoiced={() => { loadStockItems(); }}
                      newOrderSignal={newOrderSignal}
                    />
                  )
                ) : currentView === 'rentals' ? (
                  selectedBusiness && <RentalsTab businessId={selectedBusiness.id} />
                ) : currentView === 'orders' ? (
                  <div className="p-4 sm:p-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif' }}>
                    {(() => {
                      // A "quote request" is an order that still has unpriced (TBD) lines —
                      // it becomes a real PO once the supplier's pricing is filled in.
                      const isQuoteRequest = (order: Order) => {
                        const items = order.items || [];
                        return items.length > 0 && items.some(i => !i.unit_price || i.unit_price <= 0);
                      };
                      const q = orderSearchQuery.trim().toLowerCase();
                      const searchFiltered = recentOrders.filter(order => {
                        if (!q) return true;
                        const hay = [
                          `${order.supplier?.first_name ?? ''} ${order.supplier?.last_name ?? ''}`,
                          order.order_number,
                          order.project?.title ?? '',
                          String(order.total_amount),
                        ].join(' ').toLowerCase();
                        return hay.includes(q);
                      });
                      const filteredOrders = searchFiltered.filter(order => orderStatusFilter === 'all' || order.status === orderStatusFilter);
                      const fmtMoney = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      return (
                        <>
                          {/* Header */}
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Purchase Orders</h3>
                            <button
                              type="button"
                              onClick={() => setIsPlaceOrderModalOpen(true)}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[10px] bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 active:bg-blue-800 shadow-sm touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent', minHeight: '36px' }}
                            >
                              <Plus className="h-4 w-4" /> New Order
                            </button>
                          </div>

                          {/* Filters */}
                          <div className="py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                            <div className="relative flex-1">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                value={orderSearchQuery}
                                onChange={(e) => setOrderSearchQuery(e.target.value)}
                                placeholder="Search supplier, PO #, project"
                                className="w-full pl-9 pr-3 py-2.5 bg-gray-100 border-0 rounded-[10px] text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
                              />
                            </div>
                            <select
                              value={orderStatusFilter}
                              onChange={(e) => setOrderStatusFilter(e.target.value as 'all' | 'pending' | 'received' | 'cancelled')}
                              className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            >
                              <option value="all">All Statuses</option>
                              <option value="pending">Pending</option>
                              <option value="received">Received</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>

                          {/* List */}
                          {loadingOrders ? (
                            <div className="py-16 text-center text-gray-500 text-sm">Loading orders…</div>
                          ) : filteredOrders.length === 0 ? (
                            <div className="py-16 text-center">
                              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                              <p className="text-gray-500 text-sm mb-4">
                                {recentOrders.length === 0 ? 'No purchase orders yet.' : 'No orders match your filters.'}
                              </p>
                              {recentOrders.length === 0 && (
                                <button
                                  type="button"
                                  onClick={() => setIsPlaceOrderModalOpen(true)}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[10px] bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 shadow-sm touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent', minHeight: '36px' }}
                                >
                                  <Plus className="h-4 w-4" /> Place First Order
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden divide-y divide-gray-100">
                              {filteredOrders.map(order => {
                                const items = order.items || [];
                                const receivedCount = items.filter(i => (i.received_quantity ?? 0) >= i.quantity).length;
                                const totalItems = items.length;
                                const quote = isQuoteRequest(order);
                                return (
                                  <div
                                    key={order.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelectedOrderForModal(order)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedOrderForModal(order); } }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer touch-manipulation"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[15px] font-semibold text-gray-900 truncate">
                                          {`${order.supplier?.first_name ?? ''} ${order.supplier?.last_name ?? ''}`.trim() || 'Unknown Supplier'}
                                        </span>
                                        <span className="text-[12px] text-gray-500 truncate">· {order.order_number}</span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                        <span className="text-[12px] text-gray-500">{formatDate(order.created_at || '')}</span>
                                        <span className="text-[11px] text-gray-400">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
                                        {totalItems > 0 && order.status !== 'cancelled' && (
                                          <span className={`text-[11px] font-medium ${receivedCount === totalItems ? 'text-green-600' : 'text-amber-600'}`}>
                                            {receivedCount} of {totalItems} received
                                          </span>
                                        )}
                                        <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${
                                          quote ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-600'
                                        }`}>
                                          {quote ? 'Quote Request' : 'Purchase Order'}
                                        </span>
                                        {order.project && (
                                          <span className="inline-block px-1.5 py-0.5 rounded bg-purple-50 text-[11px] font-medium text-purple-600 truncate max-w-[160px]" title={`Project: ${order.project.title}`}>
                                            {order.project.title}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <div className="text-[15px] font-bold text-gray-900">
                                        {quote && order.total_amount === 0 ? 'TBD' : fmtMoney(order.total_amount)}
                                      </div>
                                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${
                                        order.status === 'pending'
                                          ? 'bg-orange-50 text-orange-700'
                                          : order.status === 'received'
                                            ? 'bg-green-50 text-green-700'
                                            : 'bg-red-50 text-red-700'
                                      }`}>
                                        {order.status === 'pending' ? 'Pending' : order.status === 'received' ? 'Received' : 'Cancelled'}
                                      </span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    })()}
                          </div>
                ) : currentView === 'history' ? (
                  <div className="p-4 sm:p-6 space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Transaction History</h3>
                    </div>

                    {/* Search & Filter Section */}
                    <div className="space-y-3 sm:space-y-4">
                      {/* Search Input */}
                      <div className="relative">
                        <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search transactions..."
                          value={historySearchQuery}
                          onChange={(e) => setHistorySearchQuery(e.target.value)}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-gray-300 rounded-xl text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                      
                      {/* Filter Buttons - iOS Style */}
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => setSelectedHistoryType(null)}
                          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-colors touch-manipulation ${
                            !selectedHistoryType
                              ? 'bg-blue-600 text-white active:bg-blue-700'
                              : 'text-gray-700 bg-white border border-gray-300 active:bg-gray-50'
                          }`}
                          style={{ minHeight: '36px' }}
                        >
                          All
                        </button>
                        <button
                          onClick={() => setSelectedHistoryType('Received')}
                          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-colors touch-manipulation ${
                            selectedHistoryType === 'Received'
                              ? 'bg-blue-600 text-white active:bg-blue-700'
                              : 'text-gray-700 bg-white border border-gray-300 active:bg-gray-50'
                          }`}
                          style={{ minHeight: '36px' }}
                        >
                          Received
                        </button>
                        <button
                          onClick={() => setSelectedHistoryType('Withdrawn')}
                          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-colors touch-manipulation ${
                            selectedHistoryType === 'Withdrawn'
                              ? 'bg-blue-600 text-white active:bg-blue-700'
                              : 'text-gray-700 bg-white border border-gray-300 active:bg-gray-50'
                          }`}
                          style={{ minHeight: '36px' }}
                        >
                          Withdrawn
                        </button>
                        <button
                          onClick={() => setSelectedHistoryType('Adjusted')}
                          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-colors touch-manipulation ${
                            selectedHistoryType === 'Adjusted'
                              ? 'bg-blue-600 text-white active:bg-blue-700'
                              : 'text-gray-700 bg-white border border-gray-300 active:bg-gray-50'
                          }`}
                          style={{ minHeight: '36px' }}
                        >
                          Adjusted
                        </button>
                        <button
                          onClick={() => setSelectedHistoryType('Product Sale')}
                          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-colors touch-manipulation ${
                            selectedHistoryType === 'Product Sale'
                              ? 'bg-blue-600 text-white active:bg-blue-700'
                              : 'text-gray-700 bg-white border border-gray-300 active:bg-gray-50'
                          }`}
                          style={{ minHeight: '36px' }}
                        >
                          Sales
                        </button>
                                    </div>
                    </div>

                    {/* History Content */}
                    {historyError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                        {historyError}
                      </div>
                    )}

                    {historyLoading ? (
                      <div className="h-40 sm:h-60 flex items-center justify-center">
                        <div className="text-center px-4">
                          <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mx-auto mb-3 sm:mb-4 animate-pulse" />
                          <p className="text-sm sm:text-base text-gray-600">Loading transactions...</p>
                        </div>
                      </div>
                    ) : groupedTransactionHistory.length === 0 ? (
                      <div className="h-40 sm:h-60 flex items-center justify-center">
                        <div className="text-center px-4">
                          <Clock className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">No Transactions Found</h4>
                          <p className="text-sm sm:text-base text-gray-500 max-w-sm mx-auto">
                            {historySearchQuery || selectedHistoryType 
                              ? 'Try adjusting your search or filter.'
                              : 'Stock transactions will appear here as you manage inventory.'
                            }
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2 sm:space-y-3">
                        {groupedTransactionHistory.map((entry) => (
                          <div key={entry.id}>
                            {/* Main Transaction Entry - iOS Style */}
                            <div 
                              className={`bg-white rounded-2xl p-3 sm:p-4 border border-gray-200/50 active:bg-gray-50 transition-colors shadow-sm touch-manipulation ${
                                entry.transaction_type === 'Grouped Sale' ? 'cursor-pointer' : ''
                              }`}
                              onClick={() => {
                                if (entry.transaction_type === 'Grouped Sale' && entry.sale_number) {
                                  toggleSaleExpansion(entry.sale_number);
                                }
                              }}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-2 sm:space-x-3 flex-1">
                                  {/* Icon based on transaction type - iOS Style */}
                                  <div className={`p-2 sm:p-2.5 rounded-xl flex-shrink-0 ${
                                    entry.transaction_type === 'Stock Transaction'
                                      ? entry.type === 'Received' 
                                        ? 'bg-green-100 text-green-600'
                                        : entry.type === 'Withdrawn'
                                        ? 'bg-red-100 text-red-600'
                                        : 'bg-blue-100 text-blue-600'
                                      : 'bg-purple-100 text-purple-600' // Product Sale or Grouped Sale
                                  }`}>
                                    {entry.transaction_type === 'Stock Transaction' ? (
                                      entry.type === 'Received' ? (
                                        <ArrowUp className="h-4 w-4 sm:h-4 sm:w-4" />
                                      ) : entry.type === 'Withdrawn' ? (
                                        <ArrowDown className="h-4 w-4 sm:h-4 sm:w-4" />
                                      ) : (
                                        <Package2 className="h-4 w-4 sm:h-4 sm:w-4" />
                                      )
                                    ) : (
                                      <ShoppingCart className="h-4 w-4 sm:h-4 sm:w-4" />
                                    )}
                                  </div>
                                  
                                  <div className="flex-1 min-w-0">
                                    {/* Header with title and badges */}
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1">
                                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 truncate mb-1 sm:mb-0">
                                        {entry.transaction_type === 'Stock Transaction' 
                                          ? entry.item_name 
                                          : entry.transaction_type === 'Grouped Sale'
                                          ? `Product Sale - ${entry.sale_number}`
                                          : `Sale ${entry.sale_number}`
                                        }
                                      </h4>
                                      
                                      {/* Mobile badges row */}
                                      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                        {/* Expand/Collapse icon for grouped sales */}
                                        {entry.transaction_type === 'Grouped Sale' && entry.sale_number && (
                                          <ChevronDown 
                                            className={`h-3 w-3 sm:h-4 sm:w-4 text-gray-500 transition-transform duration-200 ${
                                              expandedSales.has(entry.sale_number) ? 'rotate-180' : ''
                                            }`}
                                          />
                                        )}
                                      
                                        {/* Transaction type badge */}
                                        <span className={`px-2 sm:px-2.5 py-1 text-[11px] sm:text-xs font-semibold rounded-full ${
                                          entry.transaction_type === 'Stock Transaction'
                                            ? 'bg-gray-100 text-gray-600'
                                            : entry.transaction_type === 'Grouped Sale'
                                            ? 'bg-purple-100 text-purple-600'
                                            : 'bg-purple-100 text-purple-600'
                                        }`}>
                                          {entry.transaction_type === 'Stock Transaction' 
                                            ? entry.type 
                                            : entry.transaction_type === 'Grouped Sale'
                                            ? 'Sale'
                                            : 'Sale'
                                          }
                                        </span>
                                      
                                        {/* Grouped indicator */}
                                        {entry.is_grouped && (
                                          <span className="px-2 sm:px-2.5 py-1 text-[11px] sm:text-xs font-semibold bg-blue-100 text-blue-600 rounded-full">
                                            {entry.related_transactions?.length || 0} item{(entry.related_transactions?.length || 0) > 1 ? 's' : ''}
                                          </span>
                                        )}
                                      
                                        {/* Payment status for product sales */}
                                        {(entry.transaction_type === 'Product Sale' || entry.transaction_type === 'Grouped Sale') && entry.payment_status && (
                                          <span className={`px-2 sm:px-2.5 py-1 text-[11px] sm:text-xs font-semibold rounded-full ${
                                            entry.payment_status === 'paid' 
                                              ? 'bg-green-100 text-green-600'
                                              : entry.payment_status === 'unpaid'
                                              ? 'bg-red-100 text-red-600'
                                              : 'bg-yellow-100 text-yellow-600'
                                          }`}>
                                            {entry.payment_status}
                                          </span>
                                        )}
                                      
                                        {/* Deleted item indicator */}
                                        {entry.transaction_type === 'Stock Transaction' && (entry.is_item_deleted || false) && (
                                          <span className="px-2 sm:px-2.5 py-1 text-[11px] sm:text-xs font-semibold bg-red-100 text-red-600 rounded-full">
                                            Deleted
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Details row */}
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0 text-xs text-gray-600">
                                      {entry.transaction_type === 'Stock Transaction' ? (
                                        <>
                                          <span className={`font-medium ${
                                            entry.type === 'Received' 
                                              ? 'text-green-600'
                                              : entry.type === 'Withdrawn'
                                              ? 'text-red-600'
                                              : 'text-blue-600'
                                          }`}>
                                            {entry.type === 'Received' ? '+' : entry.type === 'Withdrawn' ? '-' : ''}
                                            {Math.abs(entry.quantity_change || 0)} {entry.item_unit}
                                          </span>
                                          {entry.category_name && (
                                            <span className="text-purple-600">{entry.category_name}</span>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          {entry.customer_name && (
                                            <span className="font-medium text-gray-700">
                                              Customer: {entry.customer_name}
                                            </span>
                                          )}
                                          {entry.total_amount && (
                                            <span className="font-medium text-green-600">
                                              ${entry.total_amount.toFixed(2)}
                                            </span>
                                          )}
                                          {entry.total_items_sold && (
                                            <span className="text-gray-600">
                                              {entry.total_items_sold} unit{entry.total_items_sold > 1 ? 's' : ''} sold
                                            </span>
                                          )}
                                        </>
                                      )}
                                      <span className="text-xs sm:text-xs font-medium text-gray-500">{format(new Date(entry.created_at), 'MMM d, yyyy h:mm a')}</span>
                                    </div>
                                    
                                    {/* Notes for stock transactions */}
                                    {entry.transaction_type === 'Stock Transaction' && entry.notes && (
                                      <p className="text-xs text-gray-500 mt-1 truncate">{entry.notes}</p>
                                    )}
                                    
                                    {/* Sale info for transactions linked to product sales */}
                                    {entry.transaction_type === 'Stock Transaction' && entry.sale_number && (
                                      <div className="text-xs mt-1 space-y-0.5">
                                        <p className="text-purple-600 font-medium">
                                          Sale: {entry.sale_number}
                                          {entry.total_amount && (
                                            <span className="ml-2 text-green-600">${entry.total_amount.toFixed(2)}</span>
                                          )}
                                        </p>
                                        {entry.customer_name && (
                                          <p className="text-blue-600">Customer: {entry.customer_name}</p>
                                        )}
                                        {entry.payment_status && (
                                          <p className="text-gray-600">
                                            <span className={`font-medium ${
                                              entry.payment_status === 'paid' ? 'text-green-600' : 
                                              entry.payment_status === 'unpaid' ? 'text-red-600' : 'text-yellow-600'
                                            }`}>
                                              {entry.payment_status.charAt(0).toUpperCase() + entry.payment_status.slice(1)}
                                            </span>
                                          </p>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Customer email for product sales */}
                                    {(entry.transaction_type === 'Product Sale' || entry.transaction_type === 'Grouped Sale') && entry.customer_email && (
                                      <p className="text-xs text-gray-500 mt-1 truncate">{entry.customer_email}</p>
                                    )}
                                    
                                    {/* Created by */}
                                    {entry.created_by_email && (
                                      <p className="text-xs text-gray-400 mt-1">by {entry.created_by_email}</p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Delete Button - iOS Style */}
                                {(entry.transaction_type === 'Stock Transaction' || entry.transaction_type === 'Grouped Sale') && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const confirmMessage = entry.transaction_type === 'Grouped Sale' 
                                        ? 'Are you sure you want to delete this sale? This will delete both the sale record and all related stock transactions. This action cannot be undone.'
                                        : 'Are you sure you want to delete this history entry? This action cannot be undone.';
                                      
                                      if (window.confirm(confirmMessage)) {
                                        if (entry.transaction_type === 'Grouped Sale' && entry.related_transactions?.length) {
                                          // Delete grouped transaction (sale + stock transactions)
                                          const firstStockTransaction = entry.related_transactions[0];
                                          if (firstStockTransaction && firstStockTransaction.product_sale_id) {
                                            deleteGroupedTransaction(firstStockTransaction.id, firstStockTransaction.product_sale_id)
                                              .then(() => {
                                                loadTransactionHistory();
                                                // Silent refresh of products tab to update stock quantities
                                                loadStockItems().then(() => {
                                                  console.log('✅ Stock items refreshed after grouped transaction deletion');
                                                }).catch(error => {
                                                  console.error('❌ Error refreshing stock items after deletion:', error);
                                                });
                                              })
                                              .catch(err => {
                                                console.error('Error deleting grouped transaction:', err);
                                                setHistoryError('Failed to delete transaction. Please try again.');
                                              });
                                          }
                                        } else if (entry.transaction_type === 'Stock Transaction') {
                                          // Check if this stock transaction is linked to a product sale
                                          if (entry.product_sale_id) {
                                            // Delete both the stock transaction and the product sale
                                            deleteGroupedTransaction(entry.id, entry.product_sale_id)
                                              .then(() => {
                                                loadTransactionHistory();
                                                // Silent refresh of products tab to update stock quantities
                                                loadStockItems().then(() => {
                                                  console.log('✅ Stock items refreshed after transaction with product sale deletion');
                                                }).catch(error => {
                                                  console.error('❌ Error refreshing stock items after deletion:', error);
                                                });
                                              })
                                              .catch(err => {
                                                console.error('Error deleting transaction with product sale:', err);
                                                setHistoryError('Failed to delete transaction. Please try again.');
                                              });
                                          } else {
                                            // Delete single stock transaction (no product sale linked)
                                            const stockEntry: StockHistoryEntry = {
                                              id: entry.id,
                                              item_id: entry.item_id!,
                                              type: entry.type === 'Received' ? 'purchase' : entry.type === 'Withdrawn' ? 'sale' : 'adjustment',
                                              quantity: Math.abs(entry.quantity_change || 0),
                                              price: 0,
                                              notes: entry.notes || null,
                                              created_at: entry.created_at,
                                              created_by: null,
                                              item_name: entry.item_name!,
                                              item_description: null,
                                              item_unit: entry.item_unit!,
                                              category_name: entry.category_name || null,
                                              supplier_name: null,
                                              created_by_email: entry.created_by_email || null,
                                              transaction_type: entry.type,
                                              quantity_change: entry.quantity_change || 0,
                                              is_item_deleted: entry.is_item_deleted || false
                                            };
                                            handleDeleteHistoryEntry(stockEntry);
                                          }
                                        }
                                      }
                                    }}
                                    disabled={deleteLoading}
                                    className="flex items-center justify-center w-9 h-9 text-gray-400 active:text-red-500 active:bg-red-50 rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                                    style={{ minHeight: '36px', minWidth: '36px' }}
                                    title={entry.transaction_type === 'Grouped Sale' ? 'Delete sale and related transactions' : 'Delete entry'}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Related Stock Transactions (for grouped sales) - iOS Style */}
                            {entry.is_grouped && entry.related_transactions && entry.related_transactions.length > 0 && 
                             entry.sale_number && expandedSales.has(entry.sale_number) && (
                              <div className="ml-4 sm:ml-8 mt-2 space-y-2 bg-gray-50/50 rounded-2xl p-3 sm:p-4">
                                {entry.related_transactions.map((relatedTransaction) => (
                                  <div
                                    key={relatedTransaction.id}
                                    className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200/50"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="p-2 rounded-xl bg-red-100 text-red-600 flex-shrink-0">
                                        <ArrowDown className="h-4 w-4" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                          <span className="text-sm font-semibold text-gray-900 truncate">{relatedTransaction.item_name}</span>
                                          <span className="text-sm text-red-600 font-semibold">
                                            -{Math.abs(relatedTransaction.quantity_change || 0)} {relatedTransaction.item_unit}
                                          </span>
                                          {relatedTransaction.category_name && (
                                            <span className="text-[11px] text-purple-600 font-medium">{relatedTransaction.category_name}</span>
                                          )}
                                        </div>
                                        {relatedTransaction.notes && (
                                          <p className="text-xs text-gray-500 truncate mt-1">{relatedTransaction.notes}</p>
                                        )}
                                      </div>
                                    </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
                        ))}
                </div>
              )}
            </div>
              ) : currentView === 'metrics' ? (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Inventory Metrics</h3>
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                    {/* Inventory Cost (Acquisition Cost) */}
                    <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-medium text-gray-600 mb-2">Inventory Cost</p>
                          <p className="text-4xl font-bold text-blue-600">
                            ${items.reduce((total, item) => total + (item.track_inventory === false ? 0 : item.quantity * (item.acquisition_cost || 0)), 0).toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-500 mt-2">Total acquisition cost of all items in stock</p>
                        </div>
                        <div className="p-4 bg-blue-100 rounded-2xl">
                          <DollarSign className="h-8 w-8 text-blue-600" />
                        </div>
                      </div>
                    </div>

                    {/* Inventory Value (Retail Value) */}
                    <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-medium text-gray-600 mb-2">Inventory Value</p>
                          <p className="text-4xl font-bold text-green-600">
                            ${items.reduce((total, item) => total + (item.quantity * (item.price || 0)), 0).toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-500 mt-2">Total retail value of all items in stock</p>
                        </div>
                        <div className="p-4 bg-green-100 rounded-2xl">
                          <BarChart3 className="h-8 w-8 text-green-600" />
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Coming Soon Section */}
                  <div className="bg-white rounded-lg p-8 border border-gray-200 text-center shadow-sm">
                    <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">More Metrics Coming Soon</h4>
                    <p className="text-gray-500 max-w-md mx-auto">
                      Advanced analytics, trends, forecasting, and detailed reports will be available here.
                    </p>
                  </div>

                </div>
              ) : currentView === 'suppliers' ? (
                <div className="flex flex-1 h-full overflow-hidden" style={{ minHeight: 0 }}>
                  {/* Left Sidebar - Suppliers List - Hidden on mobile when supplier selected */}
                  <div className={`${selectedSupplier ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-96 border-r border-gray-200/60 bg-white flex-col flex-shrink-0`}>
                    {/* Header */}
                    <div className="p-4 border-b border-gray-200/60 bg-gray-50/50">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                          Suppliers
                        </h2>
                        <button
                          onClick={() => setIsAddSupplierModalOpen(true)}
                          className="p-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-95 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Plus className="h-5 w-5" />
                        </button>
                      </div>
                      
                      {/* Search Bar */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search suppliers..."
                          value={suppliersSearchQuery}
                          onChange={(e) => setSuppliersSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-150 text-sm"
                          style={{ fontSize: '15px' }}
                        />
                      </div>
                    </div>

                    {/* Suppliers List */}
                    <div className="flex-1 overflow-y-auto">
                      {suppliersError && (
                        <div className="m-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm">
                          {suppliersError}
                        </div>
                      )}

                      {suppliersLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600 mx-auto"></div>
                            <p className="mt-3 text-sm text-gray-500">Loading...</p>
                          </div>
                        </div>
                      ) : (() => {
                        const filteredSuppliers = suppliers.filter(supplier => {
                          const searchLower = suppliersSearchQuery.toLowerCase();
                          return (
                            supplier.first_name.toLowerCase().includes(searchLower) ||
                            supplier.last_name.toLowerCase().includes(searchLower) ||
                            (supplier.email || '').toLowerCase().includes(searchLower) ||
                            (supplier.phone || '').toLowerCase().includes(searchLower)
                          );
                        });

                        return filteredSuppliers.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                              <Users2 className="h-8 w-8 text-gray-400" />
                            </div>
                            <h3 className="text-base font-semibold text-gray-900 mb-1">
                              {suppliersSearchQuery ? 'No suppliers found' : 'No suppliers yet'}
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">
                              {suppliersSearchQuery 
                                ? 'Try a different search' 
                                : 'Add your first supplier to get started'
                              }
                            </p>
                            {!suppliersSearchQuery && (
                              <button
                                onClick={() => setIsAddSupplierModalOpen(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-semibold"
                              >
                                <Plus className="h-4 w-4 mr-1.5 inline" />
                                Add Supplier
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {filteredSuppliers.map(supplier => (
                              <button
                                key={supplier.id}
                                onClick={() => setSelectedSupplier(supplier)}
                                className={`w-full p-4 text-left transition-colors duration-150 ${
                                  selectedSupplier?.id === supplier.id
                                    ? 'bg-blue-50/50 border-l-4 border-l-blue-600'
                                    : 'hover:bg-gray-50/50 active:bg-gray-100/50'
                                }`}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    selectedSupplier?.id === supplier.id
                                      ? 'bg-blue-600'
                                      : 'bg-gray-200'
                                  }`}>
                                    <span className={`text-lg font-semibold ${
                                      selectedSupplier?.id === supplier.id
                                        ? 'text-white'
                                        : 'text-gray-600'
                                    }`}>
                                      {supplier.first_name?.[0]?.toUpperCase() || 'S'}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <h3 className={`text-base font-semibold truncate ${
                                        selectedSupplier?.id === supplier.id
                                          ? 'text-blue-900'
                                          : 'text-gray-900'
                                      }`}>
                                        {supplier.first_name} {supplier.last_name}
                                      </h3>
                                      {supplier.type === 'business' && (
                                        <Building2 className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                      )}
                                    </div>
                                    {supplier.email && (
                                      <p className="text-xs text-gray-500 truncate">
                                        {supplier.email}
                                      </p>
                                    )}
                                  </div>
                                  {selectedSupplier?.id === supplier.id && (
                                    <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0" />
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Right Panel - Action Dashboard */}
                  <div className={`${selectedSupplier ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-gray-50/30 min-w-0`}>
                    {selectedSupplier ? (
                      <div className="flex-1 flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="p-6 border-b border-gray-200/60 bg-white">
                          {/* Back button for mobile */}
                          <button
                            onClick={() => setSelectedSupplier(null)}
                            className="md:hidden flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                          >
                            <ChevronLeft className="h-5 w-5" />
                            <span className="text-sm font-medium">Back to Suppliers</span>
                          </button>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                                <span className="text-lg font-semibold text-white">
                                  {selectedSupplier.first_name?.[0]?.toUpperCase() || 'S'}
                                </span>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                                  {selectedSupplier.first_name} {selectedSupplier.last_name}
                                </h3>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  {selectedSupplier.type === 'business' ? 'Business Supplier' : 'Individual Supplier'}
                                </p>
                              </div>
                            </div>
                            <button className="p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors">
                              <MoreVertical className="h-5 w-5 text-gray-400" />
                            </button>
                          </div>
                          
                          {/* New Order Button */}
                          <button
                            onClick={async () => {
                              // Load on-order quantities before opening modal to ensure one-time items with orders are visible
                              await loadOnOrderQuantities();
                              setIsCreateOrderModalOpen(true);
                            }}
                            className="w-full p-3.5 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2 shadow-sm font-semibold"
                            style={{ WebkitTapHighlightColor: 'transparent', fontSize: '15px' }}
                          >
                            <ShoppingCart className="h-5 w-5" />
                            New Order
                          </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto">
                          {/* Contact Info - Editable Form */}
                          <div className="px-6 pb-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                Contact Information
                              </h4>
                              {!isEditingSupplier && (
                                <button
                                  onClick={() => {
                                    setIsEditingSupplier(true);
                                    setSupplierEditForm({
                                      first_name: selectedSupplier.first_name || '',
                                      last_name: selectedSupplier.last_name || '',
                                      email: selectedSupplier.email || '',
                                      phone: selectedSupplier.phone || '',
                                      address: selectedSupplier.address || ''
                                    });
                                  }}
                                  className="text-xs font-medium text-blue-600 hover:text-blue-700"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                            <div className="space-y-2.5">
                              {/* First Name & Last Name in one row */}
                              <div className="grid grid-cols-2 gap-2.5">
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    First Name
                                  </label>
                                  <input
                                    type="text"
                                    value={supplierEditForm.first_name}
                                    onChange={(e) => setSupplierEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                                    disabled={!isEditingSupplier}
                                    className={`w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                                      isEditingSupplier ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                                    }`}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    Last Name
                                  </label>
                                  <input
                                    type="text"
                                    value={supplierEditForm.last_name}
                                    onChange={(e) => setSupplierEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                                    disabled={!isEditingSupplier}
                                    className={`w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                                      isEditingSupplier ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                                    }`}
                                  />
                                </div>
                              </div>

                              {/* Email */}
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                  Email
                                </label>
                                <input
                                  type="email"
                                  value={supplierEditForm.email}
                                  onChange={(e) => setSupplierEditForm(prev => ({ ...prev, email: e.target.value }))}
                                  disabled={!isEditingSupplier}
                                  className={`w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                                    isEditingSupplier ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                                  }`}
                                />
                              </div>

                              {/* Phone */}
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                  Phone
                                </label>
                                <input
                                  type="tel"
                                  value={supplierEditForm.phone}
                                  onChange={(e) => setSupplierEditForm(prev => ({ ...prev, phone: e.target.value }))}
                                  disabled={!isEditingSupplier}
                                  className={`w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                                    isEditingSupplier ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                                  }`}
                                />
                              </div>

                              {/* Address */}
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                  Address
                                </label>
                                <textarea
                                  value={supplierEditForm.address}
                                  onChange={(e) => setSupplierEditForm(prev => ({ ...prev, address: e.target.value }))}
                                  disabled={!isEditingSupplier}
                                  rows={2}
                                  className={`w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none ${
                                    isEditingSupplier ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                                  }`}
                                />
                              </div>

                              {/* Save/Cancel Buttons */}
                              {isEditingSupplier && (
                                <div className="flex gap-2 pt-1">
                                  <button
                                    onClick={async () => {
                                      if (!selectedSupplier?.id) return;
                                      try {
                                        setIsSavingSupplier(true);
                                        await updateCustomer(selectedSupplier.id, {
                                          first_name: supplierEditForm.first_name,
                                          last_name: supplierEditForm.last_name,
                                          email: supplierEditForm.email || null,
                                          phone: supplierEditForm.phone || null,
                                          address: supplierEditForm.address || null
                                        });
                                        // Reload suppliers to get updated data
                                        await loadSuppliers();
                                        // Update selected supplier
                                        const updatedSuppliers = await getSuppliers(selectedBusiness?.id || '');
                                        const updated = updatedSuppliers.find(s => s.id === selectedSupplier.id);
                                        if (updated) setSelectedSupplier(updated);
                                        setIsEditingSupplier(false);
                                        toast.success({
                                          title: 'Supplier Updated',
                                          description: 'Supplier information has been updated successfully.',
                                          duration: 3000
                                        });
                                      } catch (err) {
                                        console.error('Error updating supplier:', err);
                                        toast.error({
                                          title: 'Update Failed',
                                          description: err instanceof Error ? err.message : 'Failed to update supplier information',
                                          duration: 5000
                                        });
                                      } finally {
                                        setIsSavingSupplier(false);
                                      }
                                    }}
                                    disabled={isSavingSupplier}
                                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    {isSavingSupplier ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setIsEditingSupplier(false);
                                      setSupplierEditForm({
                                        first_name: selectedSupplier.first_name || '',
                                        last_name: selectedSupplier.last_name || '',
                                        email: selectedSupplier.email || '',
                                        phone: selectedSupplier.phone || '',
                                        address: selectedSupplier.address || ''
                                      });
                                    }}
                                    disabled={isSavingSupplier}
                                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-all duration-150 active:scale-[0.98] font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Previous Orders */}
                          <div className="px-6 pb-6">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-base font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                Previous Orders
                              </h4>
                              {supplierOrders.length > 0 && (
                                <button
                                  onClick={() => {
                                    // TODO: Show all orders view
                                    toast.info({ title: 'Coming Soon', description: 'View all orders functionality coming soon' });
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                                >
                                  View All
                                </button>
                              )}
                            </div>

                            {supplierOrdersLoading ? (
                              <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600"></div>
                              </div>
                            ) : supplierOrders.length === 0 ? (
                              <div className="flex flex-col items-center justify-center py-12 px-4 bg-white border border-gray-200 rounded-2xl">
                                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                  <FileText className="h-8 w-8 text-gray-400" />
                                </div>
                                <h4 className="text-base font-semibold text-gray-900 mb-1">No orders yet</h4>
                                <p className="text-sm text-gray-500 text-center max-w-xs">
                                  Orders with {selectedSupplier.first_name} will appear here
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {supplierOrders.slice(0, 5).map((order) => (
                                  <button
                                    key={order.id}
                                    onClick={() => {
                                      // TODO: Show order details
                                      toast.info({ title: 'Coming Soon', description: 'Order details functionality coming soon' });
                                    }}
                                    className="w-full p-4 bg-white border border-gray-200 rounded-2xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] text-left shadow-sm"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-900">
                                          {order.order_number}
                                        </span>
                                        <span
                                          className={`px-2 py-0.5 rounded-lg text-xs font-medium ${
                                            order.status === 'pending'
                                              ? 'bg-yellow-100 text-yellow-800'
                                              : order.status === 'received'
                                              ? 'bg-green-100 text-green-800'
                                              : 'bg-gray-100 text-gray-800'
                                          }`}
                                        >
                                          {order.status === 'pending' ? 'Pending' : order.status === 'received' ? 'Received' : 'Cancelled'}
                                        </span>
                                      </div>
                                      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs text-gray-500">
                                        {order.created_at ? format(new Date(order.created_at), 'MMM d, yyyy') : 'N/A'}
                                      </span>
                                      <span className="text-sm font-semibold text-gray-900">
                                        ${typeof order.total_amount === 'number' ? order.total_amount.toFixed(2) : '0.00'}
                                      </span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center px-4">
                          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                            <Users2 className="h-10 w-10 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a supplier</h3>
                          <p className="text-sm text-gray-500 max-w-sm">
                            Choose a supplier from the list to view details and manage orders
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : currentView === 'online-store' ? (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Online Store</h3>
                      <p className="text-sm text-gray-600 mt-1">Manage your customer-facing online store</p>
                    </div>
                    <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                      Configure Store
                    </button>
                  </div>

                  {/* Store Status */}
                  <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Store className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">Store Status</h4>
                          <p className="text-sm text-gray-600">Your online store is ready to accept orders</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium text-green-600">Active</span>
                      </div>
                    </div>
                    
                    {/* Store URL */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white rounded-lg px-3 py-2 border border-gray-300">
                          <code className="text-sm text-blue-600">
                            {window.location.origin}/{selectedBusiness?.slug || 'your-business'}/store
                          </code>
                        </div>
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => {
                              const storeUrl = `${window.location.origin}/${selectedBusiness?.slug}/store`;
                              window.open(storeUrl, '_blank');
                            }}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            Preview
                          </button>
                          <button 
                            onClick={() => {
                              const storeUrl = `${window.location.origin}/${selectedBusiness?.slug}/store`;
                              navigator.clipboard.writeText(storeUrl).then(() => {
                                toast.success({
                                  title: 'Success',
                                  description: 'Store link copied to clipboard!',
                                  duration: 3000
                                });
                              }).catch(() => {
                                // Fallback for older browsers
                                const textArea = document.createElement('textarea');
                                textArea.value = storeUrl;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                                toast.success({
                                  title: 'Success',
                                  description: 'Store link copied to clipboard!',
                                  duration: 3000
                                });
                              });
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            Copy Link
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>


                  {/* Store Configuration */}
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                      <h4 className="text-lg font-medium text-gray-900">Store Configuration</h4>
                      <p className="text-sm text-gray-600 mt-1">Configure your online store settings and appearance</p>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="border border-gray-200 rounded-lg">
                          <div className="p-4">
                            <button
                              onClick={() => setIsDesignExpanded(!isDesignExpanded)}
                              className="w-full flex items-center justify-between"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                  <Palette className="h-5 w-5 text-purple-600" />
                                </div>
                                <div className="text-left">
                                  <h5 className="font-medium text-gray-900">Store Design</h5>
                                  <p className="text-sm text-gray-600">Customize colors, logo, and layout</p>
                                </div>
                              </div>
                              <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isDesignExpanded ? 'rotate-90' : ''}`} />
                            </button>
                          </div>
                          
                          {isDesignExpanded && (
                            <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                              {/* Sidebar Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Left Sidebar Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.sidebarColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, sidebarColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.sidebarColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, sidebarColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#374151"
                                  />
                                </div>
                              </div>

                              {/* Sidebar Text Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Left Sidebar Text Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.sidebarTextColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, sidebarTextColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.sidebarTextColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, sidebarTextColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#ffffff"
                                  />
                                </div>
                              </div>

                              {/* Header Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Header Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.headerColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, headerColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.headerColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, headerColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#ffffff"
                                  />
                                </div>
                              </div>

                              {/* Background Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Main Background Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.backgroundColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.backgroundColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#f9fafb"
                                  />
                                </div>
                              </div>

                              {/* Accent Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Accent Color (Buttons & Links)
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.accentColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, accentColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.accentColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, accentColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#3b82f6"
                                  />
                                </div>
                              </div>

                              {/* Business Name Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Business Name Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.businessNameColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, businessNameColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.businessNameColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, businessNameColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#111827"
                                  />
                                </div>
                              </div>

                              {/* Browse Products Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Browse Products Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.browseProductsColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, browseProductsColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.browseProductsColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, browseProductsColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#6b7280"
                                  />
                                </div>
                              </div>

                              {/* Show Business Logo */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Show Business Logo
                                  </label>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Display your business logo in the sidebar header
                                  </p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={storeDesign.showBusinessLogo}
                                  onChange={(e) => setStoreDesign(prev => ({ ...prev, showBusinessLogo: e.target.checked }))}
                                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                              </div>

                              {/* Show Business Name */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Show Business Name
                                  </label>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Display your business name in the sidebar header
                                  </p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={storeDesign.showBusinessName}
                                  onChange={(e) => setStoreDesign(prev => ({ ...prev, showBusinessName: e.target.checked }))}
                                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                              </div>

                              {/* Display Popular Products */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Display Popular Products
                                  </label>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Show a rotating banner of featured products at the top of your store
                                  </p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={storeDesign.showPopularProducts}
                                  onChange={(e) => setStoreDesign(prev => ({ ...prev, showPopularProducts: e.target.checked }))}
                                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                              </div>

                              {/* Popular Products Selection */}
                              {storeDesign.showPopularProducts && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                  <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Select Popular Products (up to 10)
                                  </label>
                                  <p className="text-xs text-gray-500 mb-3">
                                    Choose products to feature in the rotating banner. Selected: {(storeDesign.popularProductIds || []).length}/10
                                  </p>
                                  
                                  {/* Search Bar */}
                                  <div className="relative mb-3">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                      type="text"
                                      placeholder="Search products..."
                                      value={productSearchQuery}
                                      onChange={(e) => setProductSearchQuery(e.target.value)}
                                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    />
                                  </div>

                                  {/* Products List */}
                                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                                    {isLoadingProducts ? (
                                      <div className="p-4 text-center text-sm text-gray-500">
                                        Loading products...
                                      </div>
                                    ) : (
                                      <>
                                        {availableProducts
                                          .filter(product => 
                                            product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                                            (product.description || '').toLowerCase().includes(productSearchQuery.toLowerCase())
                                          )
                                          .map(product => {
                                            const isSelected = (storeDesign.popularProductIds || []).includes(product.id);
                                            const canSelect = !isSelected && (storeDesign.popularProductIds || []).length < 10;
                                            
                                            return (
                                              <div
                                                key={product.id}
                                                className={`p-3 border-b border-gray-100 last:border-b-0 flex items-center justify-between hover:bg-gray-50 ${
                                                  isSelected ? 'bg-purple-50' : ''
                                                }`}
                                              >
                                                <div className="flex-1 min-w-0">
                                                  <h4 className="text-sm font-medium text-gray-900 truncate">
                                                    {product.name}
                                                  </h4>
                                                  <p className="text-xs text-gray-500 truncate">
                                                    ${product.price.toFixed(2)} • {product.quantity} in stock
                                                  </p>
                                                </div>
                                                <button
                                                  onClick={() => toggleProductSelection(product.id)}
                                                  disabled={!canSelect && !isSelected}
                                                  className={`ml-3 px-3 py-1 text-xs font-medium rounded transition-colors ${
                                                    isSelected
                                                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                                                      : canSelect
                                                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                  }`}
                                                >
                                                  {isSelected ? 'Remove' : 'Add'}
                                                </button>
                                              </div>
                                            );
                                          })
                                        }
                                        {availableProducts.filter(product => 
                                          product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                                          (product.description || '').toLowerCase().includes(productSearchQuery.toLowerCase())
                                        ).length === 0 && (
                                          <div className="p-4 text-center text-sm text-gray-500">
                                            {productSearchQuery ? 'No products found matching your search.' : 'No products available.'}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>

                                  {/* Selected Products Summary */}
                                  {(storeDesign.popularProductIds || []).length > 0 && (
                                    <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg">
                                      <h5 className="text-xs font-medium text-gray-700 mb-2">Selected Products:</h5>
                                      <div className="flex flex-wrap gap-1">
                                        {(storeDesign.popularProductIds || []).map(productId => {
                                          const product = availableProducts.find(p => p.id === productId);
                                          return product ? (
                                            <span
                                              key={productId}
                                              className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full"
                                            >
                                              {product.name}
                                              <button
                                                onClick={() => toggleProductSelection(productId)}
                                                className="ml-1 text-purple-600 hover:text-purple-800"
                                              >
                                                <X className="h-3 w-3" />
                                              </button>
                                            </span>
                                          ) : null;
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}


                              {/* Save Button */}
                              <div className="pt-2 border-t border-gray-200">
                                <button 
                                  onClick={saveStoreDesignSettings}
                                  disabled={isSavingDesign}
                                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                  {isSavingDesign ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                      Saving...
                                    </>
                                  ) : (
                                    'Save Design Changes'
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="border border-gray-200 rounded-lg">
                          <div className="p-4">
                            <button
                              onClick={() => setIsCatalogExpanded(!isCatalogExpanded)}
                              className="w-full flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <Package2 className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="text-left">
                                  <h5 className="font-medium text-gray-900">Product Catalog</h5>
                                  <p className="text-sm text-gray-600">Manage categories and visibility</p>
                                </div>
                              </div>
                              <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isCatalogExpanded ? 'rotate-90' : ''}`} />
                            </button>
                          </div>
                          
                          {isCatalogExpanded && (
                            <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                              {/* Stock Display Settings */}
                              <div className="space-y-4">
                                <h4 className="text-sm font-medium text-gray-700">Customer Display Settings</h4>
                                
                                {/* Display Stock Levels */}
                                <div className="flex items-center justify-between">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                      Display Stock Levels
                                    </label>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Show stock quantities to customers on the store
                                    </p>
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={storeDesign.displayStockLevels}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, displayStockLevels: e.target.checked }))}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                </div>

                                {/* Hide Out of Stock Products */}
                                <div className="flex items-center justify-between">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                      Hide Out of Stock Products
                                    </label>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Don't show products with zero stock to customers
                                    </p>
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={storeDesign.hideOutOfStockProducts}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, hideOutOfStockProducts: e.target.checked }))}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                </div>
                              </div>

                              {/* Save Button */}
                              <div className="pt-2 border-t border-gray-200">
                                <button 
                                  onClick={saveStoreDesignSettings}
                                  disabled={isSavingDesign}
                                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                  {isSavingDesign ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                      Saving...
                                    </>
                                  ) : (
                                    'Save Catalog Settings'
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="border border-gray-200 rounded-lg">
                          <div className="p-4">
                            <button
                              onClick={() => setIsShippingExpanded(!isShippingExpanded)}
                              className="w-full flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                  <CreditCard className="h-5 w-5 text-green-600" />
                                </div>
                                <div className="text-left">
                                  <h5 className="font-medium text-gray-900">Payment Settings</h5>
                                  <p className="text-sm text-gray-600">Configure payment methods and shipping</p>
                                </div>
                              </div>
                              <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isShippingExpanded ? 'rotate-180' : ''}`} />
                            </button>
                          </div>

                          {isShippingExpanded && (
                            <div className="px-4 pb-4 space-y-6">
                              {/* Shipping Options Section */}
                              <div>
                                <h6 className="text-sm font-medium text-gray-900 mb-3">Shipping Options</h6>
                                
                                {/* In-Store Pickup */}
                                <div className="space-y-3">
                                  <div className="flex items-center space-x-3">
                                    <input
                                      type="checkbox"
                                      id="enablePickup"
                                      checked={shippingSettings.enableInStorePickup}
                                      onChange={(e) => setShippingSettings(prev => ({
                                        ...prev,
                                        enableInStorePickup: e.target.checked
                                      }))}
                                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="enablePickup" className="text-sm font-medium text-gray-700">
                                      In-Store Pickup
                                    </label>
                                  </div>
                                  
                                  {shippingSettings.enableInStorePickup && (
                                    <div className="ml-7 space-y-2">
                                      <p className="text-xs text-gray-600">
                                        Using business address: <span className="font-medium">{selectedBusiness?.address || 'No address set'}</span>
                                      </p>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          Custom pickup address (optional)
                                        </label>
                                        <input
                                          type="text"
                                          value={shippingSettings.pickupAddress}
                                          onChange={(e) => setShippingSettings(prev => ({
                                            ...prev,
                                            pickupAddress: e.target.value
                                          }))}
                                          placeholder="Enter custom pickup address..."
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Additional Shipping Methods */}
                              <div>
                                <div className="flex items-center justify-between mb-3">
                                  <h6 className="text-sm font-medium text-gray-900">Additional Shipping Methods</h6>
                                  <button
                                    onClick={addShippingMethod}
                                    className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center space-x-1"
                                  >
                                    <Plus className="h-4 w-4" />
                                    <span>Add Method</span>
                                  </button>
                                </div>
                                
                                {shippingSettings.shippingMethods.length === 0 ? (
                                  <p className="text-sm text-gray-500 italic">No additional shipping methods configured</p>
                                ) : (
                                  <div className="space-y-3">
                                    {shippingSettings.shippingMethods.map((method, index) => (
                                      <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex-1">
                                          <input
                                            type="text"
                                            value={method.name}
                                            onChange={(e) => updateShippingMethod(index, 'name', e.target.value)}
                                            placeholder="Shipping method name..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                          />
                                        </div>
                                        <div className="w-24">
                                          <div className="relative">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                                            <input
                                              type="number"
                                              value={method.price}
                                              onChange={(e) => updateShippingMethod(index, 'price', parseFloat(e.target.value) || 0)}
                                              placeholder="0.00"
                                              min="0"
                                              step="0.01"
                                              className="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                            />
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => removeShippingMethod(index)}
                                          className="text-red-600 hover:text-red-700 p-1"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Save Button */}
                              <div className="pt-4 border-t border-gray-200">
                                <button
                                  onClick={saveShippingSettings}
                                  disabled={isSavingShipping}
                                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                  {isSavingShipping ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                      Saving...
                                    </>
                                  ) : (
                                    'Save Shipping Settings'
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                              <ShoppingCart className="h-5 w-5 text-orange-600" />
                            </div>
                            <div>
                              <h5 className="font-medium text-gray-900">Order Management</h5>
                              <p className="text-sm text-gray-600">Handle orders and shipping</p>
                            </div>
                          </div>
                          <button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                            Manage Orders
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              ) : null}
              </div>
            </div>

            </div>
          )}

          {/* Bottom tab bar — Inventory · Orders · Create · Purchase Orders · Transactions. */}
          <InventoryBottomNav
            active={
              currentView === 'products'
                ? 'inventory'
                : currentView === 'customer-orders'
                  ? 'orders'
                  : currentView === 'orders'
                    ? 'purchase-orders'
                    : currentView === 'history'
                      ? 'transactions'
                      : null
            }
            onInventory={() => setCurrentView('products')}
            onOrders={() => setCurrentView('customer-orders')}
            onPurchaseOrders={() => setCurrentView('orders')}
            onTransactions={() => setCurrentView('history')}
            onCreateAction={(action) => {
              if (action === 'new-item') setIsStockModalOpen(true);
              else if (action === 'receive') setIsReceiveStockModalOpen(true);
              else if (action === 'withdraw') setIsWithdrawStockModalOpen(true);
              else if (action === 'order') {
                // Jump to the Orders view and pop its New Order modal.
                setCurrentView('customer-orders');
                setNewOrderSignal((n) => n + 1);
              } else if (action === 'purchase-order') {
                setCurrentView('orders');
                setIsPlaceOrderModalOpen(true);
              }
            }}
          />
        </div>

        {/* All Modals */}
        <SelectCustomerModal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSelect={(customer) => {
            setSelectedCustomer(customer);
            setIsCustomerModalOpen(false);
          }}
        />

        <RecordProductSalePaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          onSuccess={() => {
            setIsPaymentModalOpen(false);
            clearCart();
            setSelectedCustomer(null);
            setAppliedDiscount(null);
            toast.success({
              title: "Success",
              description: "Payment processed successfully!",
              duration: 3000,
            });
            loadStockItems(); // Refresh stock quantities
          }}
          businessId={selectedBusiness?.id || ''}
          cart={cart}
          total={cartTotal}
          selectedCustomer={selectedCustomer}
          appliedDiscount={appliedDiscount}
          subtotal={cartSubtotal}
          discountAmount={discountAmount}
          taxAmount={cartTaxAmount}
        />

        <SendInvoiceModal
          isOpen={isSendInvoiceModalOpen}
          onClose={() => {
            console.log('🔍 SendInvoiceModal onClose called');
            setIsSendInvoiceModalOpen(false);
          }}
          onSelectMethod={handleSendInvoiceMethod}
          customerEmail={selectedCustomer?.email}
          customerPhone={selectedCustomer?.phone}
          customerId={selectedCustomer?.id}
          hasStripeConnected={!!((selectedBusiness as any)?.stripe_connect_account_id && (selectedBusiness as any)?.online_payments_enabled)}
          isLoading={isSendingInvoice}
          loadingMethod={isSendingInvoice ? 'email' : null}
          bookingTotal={cartTotal}
          amountPaid={0}
        />

        <NewStockItemModal
          isOpen={isStockModalOpen}
          onClose={() => setIsStockModalOpen(false)}
          onSuccess={() => {
            setIsStockModalOpen(false);
            loadStockItems();
          }}
        />

        {/* Unified editor — clicking a simple item opens the same view as creating */}
        <NewStockItemModal
          isOpen={!!editingItem}
          editItem={editingItem}
          editPhotoUrl={editingItem ? (productPhotos[editingItem.id] || null) : null}
          onClose={() => setEditingItem(null)}
          onSuccess={() => {
            setEditingItem(null);
            loadStockItems();
          }}
        />

        <AddSupplierModal
          isOpen={isAddSupplierModalOpen}
          onClose={() => setIsAddSupplierModalOpen(false)}
          onSuccess={(supplier: Customer) => {
            setIsAddSupplierModalOpen(false);
            loadSuppliers();
            // Optionally select the newly created supplier
            setSelectedSupplier(supplier);
          }}
        />

        <ReceiveStockModal
          isOpen={isReceiveStockModalOpen}
          onClose={() => setIsReceiveStockModalOpen(false)}
          onSuccess={() => {
            setIsReceiveStockModalOpen(false);
            loadStockItems();
            // Also refresh transaction history if we're on that tab
            if (currentView === 'history') {
              loadTransactionHistory().then(() => {
                console.log('✅ Transaction history refreshed after receiving stock');
              }).catch(error => {
                console.error('❌ Error refreshing transaction history after receiving stock:', error);
              });
            }
          }}
        />

        <WithdrawStockModal
          isOpen={isWithdrawStockModalOpen}
          onClose={() => setIsWithdrawStockModalOpen(false)}
          onSuccess={() => {
            // Don't close modal - allow multiple withdrawals
            loadStockItems();
            // Also refresh transaction history if we're on that tab
            if (currentView === 'history') {
              loadTransactionHistory().then(() => {
                console.log('✅ Transaction history refreshed after withdrawing stock');
              }).catch(error => {
                console.error('❌ Error refreshing transaction history after withdrawing stock:', error);
              });
            }
          }}
        />


        <SellableItemsModal
          isOpen={isSellableModalOpen}
          onClose={() => setIsSellableModalOpen(false)}
          onUpdate={loadStockItems}
          businessId={selectedBusiness?.id}
        />

        <PlaceOrderModal
          isOpen={isPlaceOrderModalOpen}
          onClose={() => {
            setIsPlaceOrderModalOpen(false);
            // Refresh orders after modal closes
            loadRecentOrders();
            // Refresh on order quantities
            loadOnOrderQuantities();
          }}
          items={items}
        />
        <CreateOrderModal
          isOpen={isCreateOrderModalOpen}
          onClose={() => setIsCreateOrderModalOpen(false)}
          items={items}
          preselectedSupplierId={selectedSupplier?.id || null}
          onOrderQuantities={onOrderQuantities}
          onItemsRefresh={async () => {
            // Reload on-order quantities first, then stock items
            console.log('🔄 Stock.tsx: onItemsRefresh called, reloading onOrderQuantities and items');
            // Fetch fresh quantities and update state immediately
            const freshQuantities = await getOnOrderQuantities(selectedBusiness?.id || '');
            console.log('📦 Stock.tsx: Fresh onOrderQuantities loaded:', freshQuantities);
            // Update state first
            setOnOrderQuantities(freshQuantities);
            // Then reload items with the fresh quantities
            const data = await getStockItems(selectedBusiness?.id || '', { 
              includePendingOneTimeItems: true,
              onOrderQuantities: freshQuantities
            });
            console.log('📋 Stock.tsx: Reloaded items:', data.length, 'items');
            setItems(data);
          }}
        />

        <ProductGroupModal
          isOpen={!!viewingGroup}
          onClose={() => setViewingGroup(null)}
          group={viewingGroup?.group ?? null}
          variants={viewingGroup?.variants ?? []}
          onSaved={loadStockItems}
        />

        {/* Purchase Order Detail - Right-side modal */}
        <PurchaseOrderDetailModal
          order={selectedOrderForModal}
          onClose={() => setSelectedOrderForModal(null)}
          onOrderUpdated={() => { loadRecentOrders(); loadStockItems(); loadOnOrderQuantities(); }}
          business={selectedBusiness ? { name: selectedBusiness.name, address: selectedBusiness.address ?? null, phone: selectedBusiness.phone ?? null, email: selectedBusiness.email ?? null } : null}
        />

        </div>
    </PermissionGuard>
  );
}

export default Stock;